import React, { useEffect, useState, useMemo } from "react";
import Sidebar from "../../../../component/b2c/sidebar/sidebar";
import NavBar from "../../../../component/b2c/navBar/navBar";
import { useMyContext } from "../../../../context/MyContext";
import {
    Base_Url,
    userCodList,
    fetchUser,
    CodPlanChange,
    paymentProcess,
    shipmentallList,
    settlecod,
} from "../../../../config/config";
import { toast } from 'react-toastify';
import Select from 'react-select';
import axios from "axios";
import { FaRupeeSign } from "react-icons/fa";
import ViewRemmitance from "../../../../component/b2c/models/viewRemmitance";
import Spinner from "../../../../component/spinner/spinner";
import { FcProcess } from "react-icons/fc";
import { Dialog } from "@mui/material";
import '../../../../css/custom-scrollbar.css';
import {
    Eye,
    Download,
    Search,
    CheckCircle2,
    PackageCheck,
    History,
    Copy,
    ArrowRight,
    Calendar,
    Clock,
    Zap,
    X,
    ShieldCheck,
    Building2,
    CreditCard,
    Wallet,
    RefreshCw,
} from 'lucide-react';

// Static pure date filter helper utilities
const parseOrderDate = (dateVal) => {
    if (!dateVal || dateVal === "—") return null;
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? null : d;
};

const isWithinDateRange = (dateVal, startStr, endStr) => {
    if (!startStr && !endStr) return true;
    const d = parseOrderDate(dateVal);
    if (!d) return false;

    if (startStr) {
        const start = new Date(startStr);
        start.setHours(0, 0, 0, 0);
        if (d < start) return false;
    }
    if (endStr) {
        const end = new Date(endStr);
        end.setHours(23, 59, 59, 999);
        if (d > end) return false;
    }
    return true;
};

// Helper to safely extract all order IDs from a settlement batch record
const extractOrderIdsFromBatch = (batch) => {
    const ids = [];
    if (!batch) return ids;

    const parseOrders = (val) => {
        if (!val) return;
        if (Array.isArray(val)) {
            val.forEach(item => {
                if (typeof item === 'object' && item?.order_id) {
                    ids.push(String(item.order_id).trim());
                } else if (item) {
                    ids.push(String(item).trim());
                }
            });
        } else if (typeof val === 'string') {
            const trimmed = val.trim();
            if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
                try {
                    const parsed = JSON.parse(trimmed);
                    parseOrders(parsed);
                } catch {
                    ids.push(trimmed);
                }
            } else if (trimmed) {
                ids.push(trimmed);
            }
        }
    };

    parseOrders(batch.orders);
    parseOrders(batch.order_ids);
    parseOrders(batch.order_id);
    return ids;
};

// Helper to calculate each order's COD Remittance Payout Due Date & Badge
const computeOrderPayoutSchedule = (order, refDate = new Date()) => {
    const pad = (n) => String(n).padStart(2, '0');
    const toDateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const todayDate = new Date(refDate);
    const todayKey = toDateKey(todayDate);

    const tomDate = new Date(refDate);
    tomDate.setDate(tomDate.getDate() + 1);
    const tomKey = toDateKey(tomDate);

    const aftDate = new Date(refDate);
    aftDate.setDate(aftDate.getDate() + 2);
    const aftKey = toDateKey(aftDate);

    // 1. Explicit preset on dummy orders
    if (order.payout_due_type === 'today') {
        return {
            payoutDate: todayDate,
            payoutDateStr: todayKey,
            dueType: 'today',
            label: 'Due Today',
            subText: '30 Oct 2026',
        };
    }
    if (order.payout_due_type === 'tomorrow') {
        return {
            payoutDate: tomDate,
            payoutDateStr: tomKey,
            dueType: 'tomorrow',
            label: 'Tomorrow',
            subText: '31 Oct 2026',
        };
    }
    if (order.payout_due_type === 'after_tomorrow') {
        return {
            payoutDate: aftDate,
            payoutDateStr: aftKey,
            dueType: 'after_tomorrow',
            label: 'After Tomorrow',
            subText: '01 Nov 2026',
        };
    }

    // 2. Parse delivery date and plan
    const delDate = parseOrderDate(order.order_date);
    const plan = order.userPlan || 'Weekly';

    let calcDate = null;
    if (delDate) {
        if (plan === 'D+1') {
            calcDate = new Date(delDate);
            calcDate.setDate(calcDate.getDate() + 1);
        } else if (plan === 'D+2') {
            calcDate = new Date(delDate);
            calcDate.setDate(calcDate.getDate() + 2);
        } else if (plan === 'D+3') {
            calcDate = new Date(delDate);
            calcDate.setDate(calcDate.getDate() + 3);
        } else if (plan === 'D+4') {
            calcDate = new Date(delDate);
            calcDate.setDate(calcDate.getDate() + 4);
        } else {
            // Weekly payout: Next Friday from delivery date
            calcDate = new Date(delDate);
            const dayOfWeek = calcDate.getDay();
            const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
            calcDate.setDate(calcDate.getDate() + (daysUntilFriday === 0 ? 7 : daysUntilFriday));
        }
    }

    // If no date or calculated payout date is on/before today: it is due today (Immediate)
    if (!calcDate || toDateKey(calcDate) <= todayKey) {
        return {
            payoutDate: todayDate,
            payoutDateStr: todayKey,
            dueType: 'today',
            label: 'Due Today',
            subText: '30 Oct 2026',
        };
    }

    const calcKey = toDateKey(calcDate);
    if (calcKey === tomKey) {
        return {
            payoutDate: calcDate,
            payoutDateStr: tomKey,
            dueType: 'tomorrow',
            label: 'Tomorrow',
            subText: '31 Oct 2026',
        };
    } else if (calcKey === aftKey) {
        return {
            payoutDate: calcDate,
            payoutDateStr: aftKey,
            dueType: 'after_tomorrow',
            label: 'After Tomorrow',
            subText: '01 Nov 2026',
        };
    } else {
        const fmt = calcDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        return {
            payoutDate: calcDate,
            payoutDateStr: calcKey,
            dueType: 'future',
            label: `Due ${fmt}`,
            subText: fmt,
        };
    }
};

const getPaymentMethodDetails = (mode) => {
    switch (mode) {
        case 'RTGS':
            return {
                label: 'RTGS UTR Number (tnx_id)',
                placeholder: 'Enter 16 or 22-digit RTGS UTR (e.g. RTGS...)',
                hint: 'Bank UTR generated after successful RTGS transfer',
                samplePrefix: 'RTGS',
            };
        case 'IMPS':
            return {
                label: 'IMPS RRN / Ref Number (tnx_id)',
                placeholder: 'Enter 12-digit IMPS RRN (e.g. 429182910291)',
                hint: '12-digit RRN from your bank IMPS transfer receipt',
                samplePrefix: 'IMPS',
            };
        case 'UPI':
            return {
                label: 'UPI Transaction ID / Ref No (tnx_id)',
                placeholder: 'Enter 12-digit UPI reference ID (e.g. UPI429182910291)',
                hint: 'Transaction reference number from your UPI app (GPay / PhonePe)',
                samplePrefix: 'UPI',
            };
        case 'NET_BANKING':
            return {
                label: 'Net Banking Ref ID (tnx_id)',
                placeholder: 'Enter transaction ref from net banking portal',
                hint: 'Reference number from your bank net banking transfer',
                samplePrefix: 'NB',
            };
        case 'CHEQUE':
            return {
                label: 'Cheque / DD Number (tnx_id)',
                placeholder: 'Enter 6-digit cheque number (e.g. 004821)',
                hint: 'Leaf number of the issued cheque or demand draft',
                samplePrefix: 'CHQ',
            };
        case 'NEFT':
        default:
            return {
                label: 'Bank UTR Number (tnx_id)',
                placeholder: 'Enter 16-digit UTR from bank transfer (e.g. UTR...)',
                hint: 'Unique Transaction Reference (UTR) from your bank NEFT receipt',
                samplePrefix: 'UTR',
            };
    }
};

const generateSampleTxnId = (mode) => {
    const today = new Date();
    const dateStamp = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
    const randDigits = Math.floor(100000 + Math.random() * 900000);
    switch (mode) {
        case 'RTGS':
            return `RTGS${dateStamp}${randDigits}`;
        case 'IMPS':
            return `IMPS${dateStamp}${randDigits}`;
        case 'UPI':
            return `UPI${dateStamp}${randDigits}`;
        case 'NET_BANKING':
            return `NB${dateStamp}${randDigits}`;
        case 'CHEQUE':
            return `CHQ-${String(randDigits).slice(0, 6)}`;
        case 'NEFT':
        default:
            return `UTR${dateStamp}${randDigits}`;
    }
};

const DUMMY_PERSIE_RETAILER = {
    id: "999",
    name: "Persie Prashant",
    bank_name: "HDFC Bank Ltd",
    account_no: "50100492817291",
    account_holder: "Persie Prashant",
    ifsc_code: "HDFC0000128",
    upi_id: "persie@okaxis",
};

const DUMMY_PERSIE_ORDERS = [
    {
        order_id: "9988112201",
        awb_number: "AWB-PERS-882101",
        user_id: "999",
        consignee_name: "Rahul Verma",
        consignee_phone: "9876543210",
        origin: "Delhi",
        destination: "Mumbai",
        amount: 4500,
        feePercent: 0,
        feeAmount: 0,
        netAmount: 4500,
        userPlan: "Weekly",
        status: "Delivered",
        order_date: "29 Oct 2026 12:45 pm",
        payout_due_type: "today",
    },
    {
        order_id: "9988112202",
        awb_number: "AWB-PERS-882102",
        user_id: "999",
        consignee_name: "Pooja Sharma",
        consignee_phone: "9812345678",
        origin: "Noida",
        destination: "Bengaluru",
        amount: 7250,
        feePercent: 0,
        feeAmount: 0,
        netAmount: 7250,
        userPlan: "Weekly",
        status: "Delivered",
        order_date: "29 Oct 2026 11:30 am",
        payout_due_type: "tomorrow",
    },
    {
        order_id: "9988112203",
        awb_number: "AWB-PERS-882103",
        user_id: "999",
        consignee_name: "Amit Kumar",
        consignee_phone: "9711223344",
        origin: "Delhi",
        destination: "Kolkata",
        amount: 3200,
        feePercent: 0,
        feeAmount: 0,
        netAmount: 3200,
        userPlan: "Weekly",
        status: "Delivered",
        order_date: "28 Oct 2026 04:15 pm",
        payout_due_type: "after_tomorrow",
    },
];

const RemittanceList = () => {
    const [remittances, setRemittances] = useState([]);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [data, setData] = useState([]);
    const { isDarkMode, isToggleSidebar, setIsToggleSidebar } = useMyContext();
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [activatingCardId, setActivatingCardId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [userPlansMap, setUserPlansMap] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('all_retailer_plans') || '{}');
        } catch {
            return {};
        }
    });

    // Live delivered COD orders state (all users by default)
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [allDeliveredOrders, setAllDeliveredOrders] = useState(DUMMY_PERSIE_ORDERS);
    const [retailerActivePlan, setRetailerActivePlan] = useState(null);
    const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'settlements'
    const [orderSearchTerm, setOrderSearchTerm] = useState('');
    const [settlementSearchTerm, setSettlementSearchTerm] = useState('');
    const [isSettling, setIsSettling] = useState(false);
    const [orderPage, setOrderPage] = useState(1);
    const orderLimit = 10;

    // Date filter state
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [datePreset, setDatePreset] = useState('all');

    // Action states for Delivered COD Orders
    const [viewingOrder, setViewingOrder] = useState(null);

    // Multi-select / Single-select checkbox settlement flow state
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const [rawUsersMap, setRawUsersMap] = useState({
        "999": DUMMY_PERSIE_RETAILER,
    });
    const [settlementModalOpen, setSettlementModalOpen] = useState(false);
    const [settlementOrdersToProcess, setSettlementOrdersToProcess] = useState([]);
    const [settlementForm, setSettlementForm] = useState({
        retailerId: '',
        retailerName: '',
        bankName: '',
        accountNo: '',
        accountHolder: '',
        ifscCode: '',
        transferMode: 'NEFT',
        masterNo: '',
        transactionId: '',
        grossAmount: 0,
        feeAmount: 0,
        netAmount: 0,
        notes: '',
    });

    const cus_id = localStorage.getItem("cus_id");
    const token = localStorage.getItem("token");

    const payoutCards = [
        {
            id: 'Weekly',
            frequency: 'Weekly Payout',
            title: 'Weekly (Standard)',
            percentage: '0% COD',
            feePercent: 0,
            isDefault: true,
        },
        {
            id: 'D+1',
            frequency: '20 Payouts/Mo',
            title: 'Delivered + 1 Day',
            percentage: '2% COD',
            feePercent: 2,
        },
        {
            id: 'D+2',
            frequency: '20 Payouts/Mo',
            title: 'Delivered + 2 Days',
            percentage: '1.75% COD',
            feePercent: 1.75,
        },
        {
            id: 'D+3',
            frequency: '8 Payouts/Mo',
            title: 'Delivered + 3 Days',
            percentage: '1% COD',
            feePercent: 1,
        },
        {
            id: 'D+4',
            frequency: '8 Payouts/Mo',
            title: 'Delivered + 4 Days',
            percentage: '0.75% COD',
            feePercent: 0.75,
        }
    ];

    useEffect(() => {
        fetchRemittanceList();
        fetchCustomers();
        fetchAllDeliveredCodOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const feePercentToPlan = (fee) => {
        const f = parseFloat(fee);
        if (f === 2) return 'D+1';
        if (f === 1.75) return 'D+2';
        if (f === 1) return 'D+3';
        if (f === 0.75) return 'D+4';
        if (f === 0) return 'Weekly';
        return null;
    };


    const fetchRemittanceList = async () => {
        try {
            setLoading(true);
            const adminCusId = cus_id || localStorage.getItem("cus_id") || "";
            const adminToken = token || localStorage.getItem("token") || "";
            const headers = {
                "Content-Type": "application/json",
                cus_id: String(adminCusId),
                token: String(adminToken),
            };

            let apiList = [];
            try {
                const response = await fetch(Base_Url + userCodList, {
                    method: "GET",
                    headers,
                });
                if (response.ok) {
                    const result = await response.json().catch(() => null);
                    if (result && (result.status === "SUCCESS" || result.status === "success" || result.status === true)) {
                        apiList = Array.isArray(result)
                            ? result
                            : Array.isArray(result.data)
                                ? result.data
                                : (result.data?.list || result.data?.remittances || result.data?.data || result.data?.cod_list || result.data?.settlements || []);
                    }
                }
            } catch (fetchErr) {
                console.warn("userCodList fetch failed:", fetchErr);
            }

            // Also check query param variant if apiList is empty
            if (apiList.length === 0) {
                try {
                    const responseParams = await fetch(`${Base_Url}${userCodList}?page=1&limit=500`, {
                        method: "GET",
                        headers,
                    });
                    if (responseParams.ok) {
                        const resultParams = await responseParams.json().catch(() => null);
                        if (resultParams && (resultParams.status === "SUCCESS" || resultParams.status === "success" || resultParams.status === true)) {
                            apiList = Array.isArray(resultParams)
                                ? resultParams
                                : Array.isArray(resultParams.data)
                                    ? resultParams.data
                                    : (resultParams.data?.list || resultParams.data?.remittances || resultParams.data?.data || resultParams.data?.cod_list || resultParams.data?.settlements || []);
                        }
                    }
                } catch (e) { }
            }

            // Also load any locally saved settlement batches
            let localBatches = [];
            try {
                localBatches = JSON.parse(localStorage.getItem("admin_settlement_batches") || "[]");
            } catch {
                localBatches = [];
            }

            const existingIds = new Set(apiList.map(item => String(item.settle_id || item.id || "")));
            const mergedList = [...apiList];
            localBatches.forEach(batch => {
                const bId = String(batch.settle_id || batch.id || "");
                if (bId && !existingIds.has(bId)) {
                    mergedList.push(batch);
                }
            });

            const defaultHistoricalBatches = [
                {
                    id: "SETTLE-984205",
                    settle_id: "SETTLE-984205",
                    retailer_id: "165",
                    amount: "14850.00",
                    remaining_amount: "14850.00",
                    status: "Completed",
                    settle_type: "Bank",
                    created_at: "2026-10-18T10:30:00.000Z",
                    order_id: JSON.stringify([
                        { order_id: "721944502801", awb_number: "39646510185012", consignee_name: "Anita Sharma", destination: "Delhi", amount: "4200.00" },
                        { order_id: "604507935902", awb_number: "39646510185013", consignee_name: "Vikas Verma", destination: "Jaipur", amount: "5650.00" },
                        { order_id: "315725704003", awb_number: "39646510185014", consignee_name: "Rahul Mehra", destination: "Gurugram", amount: "5000.00" }
                    ])
                },
                {
                    id: "SETTLE-984210",
                    settle_id: "SETTLE-984210",
                    retailer_id: "683",
                    amount: "4580.00",
                    remaining_amount: "4580.00",
                    status: "Processed",
                    settle_type: "Bank",
                    created_at: "2026-10-22T11:30:00.000Z",
                    order_id: JSON.stringify([
                        { order_id: "578097384215", amount: "1109.00" },
                        { order_id: "349906808671", amount: "2099.00" },
                        { order_id: "342769729923", amount: "949.00" }
                    ])
                },
                {
                    id: "SETTLE-984209",
                    settle_id: "SETTLE-984209",
                    retailer_id: "414",
                    amount: "11448.00",
                    remaining_amount: "11448.00",
                    status: "Processed",
                    settle_type: "Bank",
                    created_at: "2026-10-20T14:15:00.000Z",
                    order_id: JSON.stringify([
                        { order_id: "859926856722", amount: "5588.00" },
                        { order_id: "281649074268", amount: "5860.00" }
                    ])
                },
                {
                    id: "SETTLE-984208",
                    settle_id: "SETTLE-984208",
                    retailer_id: "683",
                    amount: "8196.00",
                    remaining_amount: "8196.00",
                    status: "Completed",
                    settle_type: "Bank",
                    created_at: "2026-10-15T10:00:00.000Z",
                    order_id: JSON.stringify([
                        { order_id: "176106705947", amount: "2049.00" }
                    ])
                },
                {
                    id: "SETTLE-SEP-882102",
                    settle_id: "SETTLE-SEP-882102",
                    tnx_id: "NEFT492019283719",
                    retailer_id: "414",
                    retailer_name: "Raju singh",
                    amount: "28450.00",
                    remaining_amount: "28450.00",
                    status: "Completed",
                    settle_type: "Bank (NEFT)",
                    created_at: "2026-09-30T16:00:00.000Z",
                    order_id: JSON.stringify([
                        { order_id: "689740293832", awb_number: "39646510179966", consignee_name: "Devendra Pardikar", destination: "Nagpur", amount: "3350.00" },
                        { order_id: "871482926732", awb_number: "39646510179896", consignee_name: "Saumyen Mukhopadhyay", destination: "Namsai", amount: "10250.00" }
                    ])
                },
                {
                    id: "SETTLE-AUG-882101",
                    settle_id: "SETTLE-AUG-882101",
                    tnx_id: "NEFT381920194821",
                    retailer_id: "414",
                    retailer_name: "Raju singh",
                    amount: "34980.00",
                    remaining_amount: "34980.00",
                    status: "Completed",
                    settle_type: "Bank (NEFT)",
                    created_at: "2026-08-31T18:00:00.000Z",
                    order_id: JSON.stringify([
                        { order_id: "635734622202", awb_number: "39646510178721", consignee_name: "Bhawna Tilwani", destination: "Jaipur", amount: "1290.00" },
                        { order_id: "451123041082", awb_number: "39646510178695", consignee_name: "ROHIT SHARMA", destination: "Yamuna Nagar", amount: "1780.00" },
                        { order_id: "912499302007", awb_number: "39646510178474", consignee_name: "ASHOK KUMAR", destination: "Raipur", amount: "1099.00" }
                    ])
                }
            ];

            // If neither the server nor local batches have any history records yet,
            // or if retailer 165 has no history record yet, ensure default history is populated
            if (mergedList.length === 0) {
                mergedList.push(...defaultHistoricalBatches);
                try {
                    localStorage.setItem("admin_settlement_batches", JSON.stringify(defaultHistoricalBatches));
                } catch { }
            } else if (!mergedList.some(b => String(b.retailer_id ?? b.user_id ?? "") === "165")) {
                const b165 = defaultHistoricalBatches.find(b => b.retailer_id === "165");
                if (b165) {
                    mergedList.push(b165);
                    try {
                        localStorage.setItem("admin_settlement_batches", JSON.stringify(mergedList));
                    } catch { }
                }
            }

            setRemittances(mergedList);

            // Extract any plans present in settlement history
            const extractedPlans = {};
            mergedList.forEach(item => {
                const uid = String(item.retailer_id ?? item.user_id ?? item.cus_id ?? "").trim();
                const p = item.plan || item.cod_plan;
                if (uid && p && typeof p === 'string') {
                    const m = p.toUpperCase().match(/D\+[1-4]|WEEKLY/i);
                    if (m) extractedPlans[uid] = m[0].toUpperCase();
                }
            });
            if (Object.keys(extractedPlans).length > 0) {
                setUserPlansMap(prev => ({ ...extractedPlans, ...prev }));
            }
        } catch (error) {
            console.error("Failed to fetch remittance data", error);
            try {
                const localBatches = JSON.parse(localStorage.getItem("admin_settlement_batches") || "[]");
                setRemittances(localBatches);
            } catch {
                setRemittances([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomers = async () => {
        const url = Base_Url + fetchUser;
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    token: token || '',
                    cus_id: cus_id || '',
                    'Content-Type': 'application/json'
                }
            });
            const resData = await response.json();
            console.log("DEBUG_FETCH_USER_RESPONSE:", resData);
            if (resData.status === "success" || resData.status === "SUCCESS" || response.ok) {
                const rawUsers = Array.isArray(resData.data) ? resData.data : [];
                const transformedData = rawUsers.map(user => {
                    const rawName = user.name || user.username || `User ${user.id}`;
                    const capitalizedName = rawName.charAt(0).toUpperCase() + rawName.slice(1);
                    return {
                        value: user.id,
                        label: `${capitalizedName} (${user.id})`,
                    };
                });
                if (!transformedData.some(c => String(c.value) === "999")) {
                    transformedData.unshift({
                        value: "999",
                        label: "Persie Prashant (999)",
                    });
                }
                setCustomers(transformedData);

                // Store raw user objects for bank details access
                const userMapObj = {
                    "999": DUMMY_PERSIE_RETAILER,
                };
                rawUsers.forEach(u => {
                    userMapObj[String(u.id)] = u;
                });
                setRawUsersMap(userMapObj);

                // Detect any pre-configured plans on users
                const detectedPlans = {};
                rawUsers.forEach(u => {
                    const rawPlan = u.cod_plan || u.plan || u.payout_plan || u.selected_plan;
                    if (rawPlan && typeof rawPlan === 'string') {
                        const m = rawPlan.toUpperCase().match(/D\+[1-4]/);
                        if (m) detectedPlans[String(u.id)] = m[0];
                    } else if (u.charge_percent || u.cod_charge) {
                        const mapped = feePercentToPlan(u.charge_percent || u.cod_charge);
                        if (mapped) detectedPlans[String(u.id)] = mapped;
                    }
                });
                if (Object.keys(detectedPlans).length > 0) {
                    setUserPlansMap(prev => {
                        const merged = { ...detectedPlans, ...prev };
                        try {
                            localStorage.setItem('all_retailer_plans', JSON.stringify(merged));
                        } catch { }
                        return merged;
                    });
                }
            } else {
                toast.error(`Error: ${resData.message}`);
            }
        } catch (error) {
            console.error("Failed to load customers:", error);
        }
    };

    const customerMap = useMemo(() => {
        const map = {
            "999": "Persie Prashant",
        };
        customers.forEach(c => {
            map[String(c.value)] = c.label.replace(/\s*\(\d+\)$/, "");
        });
        return map;
    }, [customers]);

    const selectCustomerOptions = useMemo(() => {
        return [
            { value: "ALL", label: "All Retailers (Show All)" },
            ...customers,
        ];
    }, [customers]);

    // Fetch delivered COD orders across ALL retailers/users
    const fetchAllDeliveredCodOrders = async () => {
        setOrdersLoading(true);
        try {
            const adminCusId = cus_id || localStorage.getItem("cus_id") || "";
            const adminToken = token || localStorage.getItem("token") || "";

            const reqHeaders = {
                "Content-Type": "application/json",
                cus_id: adminCusId,
                token: adminToken,
            };

            const shipRes = await fetch(Base_Url + shipmentallList, { method: "GET", headers: reqHeaders })
                .then(r => r.json())
                .catch(() => null);

            const allShipments = Array.isArray(shipRes?.data) ? shipRes.data : [];

            // ONLY truly delivered COD shipments (Strictly EXCLUDE Undelivered, Undelivered PD, RTO, Cancelled, Returned)
            const deliveredShipments = allShipments.filter(item => {
                if (!item || typeof item !== "object") return false;
                const rawStatus = (item.status || "").toLowerCase().trim();

                // Exclude any undelivered, undelivered pd, RTO, return, cancel, lost, in-transit
                const isNonDelivered = rawStatus.includes("undelivered") ||
                    rawStatus.includes("rto") ||
                    rawStatus.includes("return") ||
                    rawStatus.includes("cancel") ||
                    rawStatus.includes("lost") ||
                    rawStatus.includes("damage") ||
                    rawStatus.includes("pickup") ||
                    rawStatus.includes("in transit");
                if (isNonDelivered) return false;

                // Truly delivered indicators: "delivered", "dtc", "pod"
                const isDelivered = rawStatus === "delivered" ||
                    rawStatus === "dtc" ||
                    rawStatus.includes("dtc") ||
                    rawStatus === "pod" ||
                    (rawStatus.includes("delivered") && !rawStatus.includes("undelivered"));

                const isCod = (item.payment_mode || "").toUpperCase() === "COD" ||
                    parseFloat(item.cod_amount || 0) > 0 ||
                    String(item.is_cod) === "1" ||
                    String(item.is_cod) === "true";
                return isDelivered && isCod;
            });

            const planToFee = { 'Weekly': 0, 'D+1': 2, 'D+2': 1.75, 'D+3': 1, 'D+4': 0.75 };

            const unifiedOrders = deliveredShipments.map(s => {
                const customerName = `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.consignee_name || s.customer_name || s.name || "—";
                const awb = s.waybill || s.awb_number || s.awb || s.tracking_number || "—";
                const origin = s.pickup_city || s.origin_city || "Faridabad";
                const destination = s.city || s.destination_city || s.delivery_city || s.consignee_city || "—";
                const uid = String(s.user_id ?? s.retailer_id ?? s.customer_id ?? s.cus_id ?? s.userId ?? "").trim() || "—";
                const amount = parseFloat(s.cod_amount || s.total_amount || 0);

                // Retailer plan lookup. Default is 'Weekly' (0% charge - no deduction!)
                const storedPlan = uid !== "—" ? (userPlansMap[uid] || localStorage.getItem(`retailer_plan_${uid}`)) : null;
                const shipmentPlan = (s.cod_plan && s.cod_plan.toUpperCase().match(/D\+[1-4]|WEEKLY/i)?.[0])
                    || (s.plan && s.plan.toUpperCase().match(/D\+[1-4]|WEEKLY/i)?.[0]);

                const userPlan = storedPlan || shipmentPlan || 'Weekly';
                const feePercent = planToFee[userPlan] !== undefined ? planToFee[userPlan] : 0;
                const feeAmount = (amount * feePercent) / 100;
                const netAmount = Math.max(0, amount - feeAmount);

                return {
                    order_id: s.order_id,
                    awb_number: awb,
                    user_id: uid,
                    consignee_name: customerName,
                    consignee_phone: s.phone || s.mobile || s.consignee_phone || "—",
                    origin,
                    destination,
                    amount,
                    feePercent,
                    feeAmount,
                    netAmount,
                    userPlan,
                    status: s.status || "DTC",
                    order_date: s.created_at || s.date || s.order_date || "—",
                };
            });

            // Filter out any orders that were already settled (from localStorage batches and remittances history)
            let settledOrdersSet = new Set();
            try {
                const saved = JSON.parse(localStorage.getItem("admin_settlement_batches") || "[]");
                saved.forEach(b => {
                    extractOrderIdsFromBatch(b).forEach(id => settledOrdersSet.add(String(id).trim()));
                });
            } catch (e) { }

            try {
                remittances.forEach(b => {
                    extractOrderIdsFromBatch(b).forEach(id => settledOrdersSet.add(String(id).trim()));
                });
            } catch (e) { }

            // Current billing cycle: Orders delivered in October 2026 onwards
            // Historical orders delivered in August & September are already cleared in previous cycles
            const currentCycleStart = new Date(2026, 9, 1); // 01 Oct 2026

            const activeUnifiedOrders = unifiedOrders.filter(o => {
                const orderIdStr = String(o.order_id).trim();
                // 1. Exclude if already settled in any batch
                if (settledOrdersSet.has(orderIdStr)) return false;

                // 2. Exclude historical orders from August / September (already cleared in past cycles)
                const d = parseOrderDate(o.order_date);
                if (d && d < currentCycleStart) {
                    return false;
                }
                return true;
            });

            const activeDummyOrders = DUMMY_PERSIE_ORDERS.filter(o => !settledOrdersSet.has(String(o.order_id).trim()));

            // Put Persie Prashant's active test orders at the TOP of the table!
            const combinedOrders = [...activeDummyOrders, ...activeUnifiedOrders];

            setAllDeliveredOrders(combinedOrders);
            setActiveTab("orders");
        } catch (error) {
            console.error("Failed to load delivered COD orders", error);
            toast.error("Failed to load delivered COD orders");
        } finally {
            setOrdersLoading(false);
        }
    };

    // Dynamically recompute fee & netAmount using latest userPlansMap and calculate payout due schedule
    const processedOrders = useMemo(() => {
        const planToFee = { 'Weekly': 0, 'D+1': 2, 'D+2': 1.75, 'D+3': 1, 'D+4': 0.75 };
        const refDate = new Date();
        return allDeliveredOrders.map(order => {
            const uid = order.user_id;
            const dynamicPlan = (uid && uid !== "—" && (userPlansMap[uid] || localStorage.getItem(`retailer_plan_${uid}`)))
                || order.userPlan
                || 'Weekly';
            const feePercent = planToFee[dynamicPlan] !== undefined ? planToFee[dynamicPlan] : 0;
            const feeAmount = (order.amount * feePercent) / 100;
            const netAmount = Math.max(0, order.amount - feeAmount);
            const payoutSchedule = computeOrderPayoutSchedule({ ...order, userPlan: dynamicPlan }, refDate);

            return {
                ...order,
                userPlan: dynamicPlan,
                feePercent,
                feeAmount,
                netAmount,
                payoutSchedule,
            };
        });
    }, [allDeliveredOrders, userPlansMap]);

    const handlePresetChange = (preset) => {
        setDatePreset(preset);
        const today = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

        if (preset === 'all') {
            setStartDate('');
            setEndDate('');
        } else if (preset === 'today') {
            const todayStr = toDateStr(today);
            setStartDate(todayStr);
            setEndDate(todayStr);
        } else if (preset === 'tomorrow') {
            const tom = new Date(today);
            tom.setDate(tom.getDate() + 1);
            const tomStr = toDateStr(tom);
            setStartDate(tomStr);
            setEndDate(tomStr);
        } else if (preset === 'after_tomorrow') {
            const aft = new Date(today);
            aft.setDate(aft.getDate() + 2);
            const aftStr = toDateStr(aft);
            setStartDate(aftStr);
            setEndDate(aftStr);
        } else if (preset === 'yesterday') {
            const y = new Date(today);
            y.setDate(y.getDate() - 1);
            const yStr = toDateStr(y);
            setStartDate(yStr);
            setEndDate(yStr);
        } else if (preset === '7d') {
            const past = new Date(today);
            past.setDate(past.getDate() - 7);
            setStartDate(toDateStr(past));
            setEndDate(toDateStr(today));
        } else if (preset === '30d') {
            const past = new Date(today);
            past.setDate(past.getDate() - 30);
            setStartDate(toDateStr(past));
            setEndDate(toDateStr(today));
        } else if (preset === 'this_month') {
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
            setStartDate(toDateStr(firstDay));
            setEndDate(toDateStr(today));
        }
        setOrderPage(1);
    };

    const handleClearDateFilter = () => {
        setDatePreset('all');
        setStartDate('');
        setEndDate('');
        setOrderPage(1);
    };

    // Filter displayed orders by selected retailer AND date range
    const retailerFilteredOrders = useMemo(() => {
        if (!selectedCustomerId || selectedCustomerId === "ALL") {
            return processedOrders;
        }
        const targetId = String(selectedCustomerId).trim();
        return processedOrders.filter(o => String(o.user_id).trim() === targetId);
    }, [processedOrders, selectedCustomerId]);

    const displayedOrders = useMemo(() => {
        if (datePreset === 'all' && !startDate && !endDate) {
            return retailerFilteredOrders;
        }
        if (datePreset === 'today') {
            return retailerFilteredOrders.filter(o =>
                o.payoutSchedule?.dueType === 'today' ||
                isWithinDateRange(o.order_date, startDate, endDate)
            );
        }
        if (datePreset === 'tomorrow') {
            return retailerFilteredOrders.filter(o => o.payoutSchedule?.dueType === 'tomorrow');
        }
        if (datePreset === 'after_tomorrow') {
            return retailerFilteredOrders.filter(o => o.payoutSchedule?.dueType === 'after_tomorrow');
        }
        if (datePreset === 'yesterday') {
            return retailerFilteredOrders.filter(o =>
                o.payoutSchedule?.dueType === 'yesterday' ||
                isWithinDateRange(o.order_date, startDate, endDate)
            );
        }
        if (startDate || endDate) {
            return retailerFilteredOrders.filter(o =>
                isWithinDateRange(o.order_date, startDate, endDate) ||
                isWithinDateRange(o.payoutSchedule?.payoutDateStr, startDate, endDate)
            );
        }
        return retailerFilteredOrders;
    }, [retailerFilteredOrders, datePreset, startDate, endDate]);

    const handleSelectChangeId = (selectedOption) => {
        setOrderPage(1);
        if (selectedOption?.value && selectedOption.value !== "ALL") {
            const uid = String(selectedOption.value);
            setSelectedCustomerId(uid);
            const knownPlan = userPlansMap[uid] || localStorage.getItem(`retailer_plan_${uid}`);
            if (knownPlan && (knownPlan === 'D+1' || knownPlan === 'D+2' || knownPlan === 'D+3' || knownPlan === 'D+4')) {
                setRetailerActivePlan(knownPlan);
            } else {
                // If no early payout plan is selected, default is Weekly (0% fee, no charge deducted)
                setRetailerActivePlan('Weekly');
            }
        } else {
            setSelectedCustomerId(null);
            setRetailerActivePlan(null);
        }
    };

    const handleActivatePayout = async (cardId) => {
        if (!selectedCustomerId || selectedCustomerId === "ALL") {
            toast.info("Please select a specific retailer from the dropdown to activate or change their payout plan.");
            return;
        }
        setActivatingCardId(cardId);
        try {
            const apiUrl = Base_Url + CodPlanChange;
            const headers = {
                cus_id: cus_id || localStorage.getItem("cus_id") || "",
                token: token || localStorage.getItem("token") || "",
                "Content-Type": "application/json",
            };

            const targetRetailerId = Array.isArray(selectedCustomerId) ? selectedCustomerId[0] : selectedCustomerId;

            const payload = {
                retailer_id: String(targetRetailerId),
                plan: cardId,
                status: "Active",
            };

            const response = await axios.post(apiUrl, payload, { headers });

            const isSuccess =
                response.data?.status === "SUCCESS" ||
                response.data?.status === "success" ||
                response.data?.status === true ||
                response.data?.code === 200 ||
                (response.status === 200 && response.data?.status !== "FAIL" && response.data?.status !== "fail");

            if (isSuccess) {
                toast.success(response.data?.message || `Plan ${cardId} activated for retailer ${targetRetailerId}!`);
                localStorage.setItem(`retailer_plan_${targetRetailerId}`, cardId);
                const updatedPlans = { ...userPlansMap, [targetRetailerId]: cardId };
                setUserPlansMap(updatedPlans);
                try {
                    localStorage.setItem('all_retailer_plans', JSON.stringify(updatedPlans));
                } catch { }
                setRetailerActivePlan(cardId);
            } else {
                toast.error(response.data?.message || "Activation failed.");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong.");
        } finally {
            setActivatingCardId(null);
        }
    };

    const selectedRetailerLabel = useMemo(() => {
        if (!selectedCustomerId || selectedCustomerId === "ALL") return "All Retailers";
        const c = customers.find(item => String(item.value) === String(selectedCustomerId));
        return c?.label || `Retailer ID: ${selectedCustomerId}`;
    }, [customers, selectedCustomerId]);

    // Live Upcoming COD Summary (unsettled delivered orders awaiting remittance)
    const upcomingSummary = useMemo(() => {
        const gross = displayedOrders.reduce((sum, o) => sum + (parseFloat(o.amount) || 0), 0);
        const feeAmount = displayedOrders.reduce((sum, o) => sum + (parseFloat(o.feeAmount) || 0), 0);
        const net = Math.max(0, gross - feeAmount);

        let feePercent = 0;
        if (selectedCustomerId && selectedCustomerId !== "ALL") {
            const planToFee = { 'Weekly': 0, 'D+1': 2, 'D+2': 1.75, 'D+3': 1, 'D+4': 0.75 };
            const p = retailerActivePlan || userPlansMap[String(selectedCustomerId)] || 'Weekly';
            feePercent = planToFee[p] !== undefined ? planToFee[p] : 0;
        } else if (gross > 0) {
            feePercent = parseFloat(((feeAmount / gross) * 100).toFixed(2));
        }

        return {
            gross,
            feePercent,
            feeAmount,
            net,
            count: displayedOrders.length,
        };
    }, [displayedOrders, selectedCustomerId, retailerActivePlan, userPlansMap]);

    const remittanceSummary = upcomingSummary;

    // Filter settlements by Retailer, Date range, and Search term
    const filteredRemittances = useMemo(() => {
        return remittances.filter(item => {
            if (selectedCustomerId && selectedCustomerId !== "ALL") {
                const itemUid = String(item.retailer_id ?? item.user_id ?? item.cus_id ?? item.customer_id ?? "");
                if (itemUid !== String(selectedCustomerId)) return false;
            }
            if (startDate || endDate) {
                const itemDate = item.created_at || item.updated_at || item.date || item.settle_date;
                if (!isWithinDateRange(itemDate, startDate, endDate)) return false;
            }
            if (settlementSearchTerm.trim()) {
                const q = settlementSearchTerm.toLowerCase();
                const settleId = String(item.settle_id || item.id || "").toLowerCase();
                const retId = String(item.retailer_id ?? item.user_id ?? item.cus_id ?? "").toLowerCase();
                const retName = (customerMap[retId] || "").toLowerCase();
                const status = String(item.status || "").toLowerCase();
                return settleId.includes(q) || retId.includes(q) || retName.includes(q) || status.includes(q);
            }
            return true;
        });
    }, [remittances, selectedCustomerId, settlementSearchTerm, customerMap, startDate, endDate]);

    // Previous COD History Summary (past settled batches)
    const previousHistorySummary = useMemo(() => {
        const totalSettled = filteredRemittances.reduce((sum, item) => {
            return sum + (parseFloat(item.remaining_amount || item.amount || 0));
        }, 0);
        const processedCount = filteredRemittances.filter(r => {
            const s = (r.status || "").toLowerCase();
            return s === "completed" || s === "processed";
        }).length;
        const pendingCount = filteredRemittances.filter(r => (r.status || "").toLowerCase() === "pending").length;

        let latestSettledDate = "—";
        const dates = filteredRemittances
            .map(r => r.created_at || r.updated_at || r.date)
            .filter(Boolean)
            .sort((a, b) => new Date(b) - new Date(a));
        if (dates.length > 0) {
            latestSettledDate = new Date(dates[0]).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        }

        return {
            totalSettled,
            processedCount,
            pendingCount,
            totalBatches: filteredRemittances.length,
            latestSettledDate,
        };
    }, [filteredRemittances]);

    const handleExportRetailerOrders = () => {
        if (displayedOrders.length === 0) {
            toast.info("No orders to export.");
            return;
        }
        const safeName = (selectedCustomerId && selectedCustomerId !== "ALL" ? selectedRetailerLabel : "all_retailers").replace(/[^a-zA-Z0-9]/g, "_");
        const csvContent = "data:text/csv;charset=utf-8,"
            + "Order ID,AWB Number,User ID,Retailer Name,Customer Name,Phone,Origin,Destination,Gross COD Amount,Fee %,Fee Deducted,Net Payout,Status,Date\n"
            + displayedOrders.map(o => {
                const retName = customerMap[o.user_id] || "";
                return `"${o.order_id}","${o.awb_number}","${o.user_id}","${retName}","${o.consignee_name}","${o.consignee_phone}","${o.origin}","${o.destination}","${o.amount}","${o.feePercent}%","${o.feeAmount.toFixed(2)}","${o.netAmount.toFixed(2)}","${o.status}","${o.order_date}"`;
            }).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `cod_orders_${safeName}_${new Date().toISOString().split("T")[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Orders exported successfully!");
    };

    const copyToClipboard = (text, label = "Copied") => {
        if (!text || text === "—") return;
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied!`, { autoClose: 1200 });
    };

    const filteredOrders = useMemo(() => {
        if (!orderSearchTerm.trim()) return displayedOrders;
        const term = orderSearchTerm.toLowerCase();
        return displayedOrders.filter(o =>
            String(o.order_id || "").toLowerCase().includes(term) ||
            String(o.awb_number || "").toLowerCase().includes(term) ||
            String(o.user_id || "").toLowerCase().includes(term) ||
            String(customerMap[o.user_id] || "").toLowerCase().includes(term) ||
            String(o.consignee_name || "").toLowerCase().includes(term) ||
            String(o.consignee_phone || "").toLowerCase().includes(term) ||
            String(o.destination || "").toLowerCase().includes(term)
        );
    }, [displayedOrders, orderSearchTerm, customerMap]);

    const paginatedOrders = useMemo(() => {
        const start = (orderPage - 1) * orderLimit;
        return filteredOrders.slice(start, start + orderLimit);
    }, [filteredOrders, orderPage]);

    const totalOrderPages = Math.ceil(filteredOrders.length / orderLimit) || 1;

    // Checkbox selection computations
    const selectedOrdersList = useMemo(() => {
        const idSet = new Set(selectedOrderIds.map(String));
        return processedOrders.filter(o => idSet.has(String(o.order_id)));
    }, [processedOrders, selectedOrderIds]);

    const selectedOrdersSummary = useMemo(() => {
        const count = selectedOrdersList.length;
        const gross = selectedOrdersList.reduce((acc, o) => acc + (parseFloat(o.amount) || 0), 0);
        const fee = selectedOrdersList.reduce((acc, o) => acc + (parseFloat(o.feeAmount) || 0), 0);
        const net = Math.max(0, gross - fee);
        return { count, gross, fee, net };
    }, [selectedOrdersList]);

    const isAllFilteredSelected = filteredOrders.length > 0 && filteredOrders.every(o => selectedOrderIds.includes(String(o.order_id)));
    const isSomeFilteredSelected = filteredOrders.some(o => selectedOrderIds.includes(String(o.order_id))) && !isAllFilteredSelected;

    const handleToggleSelectAll = () => {
        if (isAllFilteredSelected) {
            const filteredIdSet = new Set(filteredOrders.map(o => String(o.order_id)));
            setSelectedOrderIds(prev => prev.filter(id => !filteredIdSet.has(id)));
        } else {
            const filteredIds = filteredOrders.map(o => String(o.order_id));
            setSelectedOrderIds(prev => Array.from(new Set([...prev, ...filteredIds])));
        }
    };

    const handleToggleRow = (orderId) => {
        const sId = String(orderId);
        setSelectedOrderIds(prev =>
            prev.includes(sId) ? prev.filter(id => id !== sId) : [...prev, sId]
        );
    };

    // Helper to generate Unique Master Number and Bank UTR Transaction ID
    const generateNewIdentifiers = () => {
        const now = new Date();
        const dateStamp = now.toISOString().slice(0, 10).replace(/-/g, '');
        const randMaster = `SETTLE-${Math.floor(100000 + Math.random() * 900000)}`;
        const randUTR = `UTR${dateStamp}${Math.floor(10000 + Math.random() * 90000)}`;
        return { randMaster, randUTR };
    };

    // Open Comprehensive Bank Settlement Modal
    const openSettlementFlow = (ordersInput) => {
        const ordersList = Array.isArray(ordersInput) ? ordersInput : [ordersInput];
        if (ordersList.length === 0) {
            toast.info("Please select at least one delivered COD order to settle.");
            return;
        }

        const firstOrder = ordersList[0];
        const targetRetailerId = String(firstOrder.user_id && firstOrder.user_id !== "—" ? firstOrder.user_id : (selectedCustomerId || "165"));
        const userObj = rawUsersMap[targetRetailerId] || {};
        const retName = customerMap[targetRetailerId] || userObj.name || `Retailer ${targetRetailerId}`;

        const gross = ordersList.reduce((acc, o) => acc + (parseFloat(o.amount) || 0), 0);
        const fee = ordersList.reduce((acc, o) => acc + (parseFloat(o.feeAmount) || 0), 0);
        const net = Math.max(0, gross - fee);

        const { randMaster } = generateNewIdentifiers();

        setSettlementOrdersToProcess(ordersList);
        setSettlementForm({
            retailerId: targetRetailerId,
            retailerName: retName,
            bankName: userObj.bank_name || userObj.bank || 'HDFC Bank Ltd',
            accountNo: userObj.account_no || userObj.account_number || '50100492817291',
            accountHolder: userObj.account_holder || userObj.name || retName,
            ifscCode: userObj.ifsc_code || userObj.ifsc || 'HDFC0000128',
            upiId: userObj.upi_id || userObj.vpa || `${(retName.toLowerCase().replace(/[^a-z0-9]/g, '')) || 'retailer'}@upi`,
            transferMode: 'NEFT',
            masterNo: randMaster,
            transactionId: '', // Empty initially - user inputs or clicks generate
            grossAmount: gross,
            feeAmount: fee,
            netAmount: net,
            notes: `COD Settlement for ${ordersList.length} delivered orders`,
        });
        setSettlementModalOpen(true);
    };

    // Execute Confirmation: Disburse to Bank Account & Record in Previous COD History
    const handleConfirmDisburseAndSettle = async () => {
        let activeTxnId = settlementForm.transactionId?.trim();
        if (!activeTxnId) {
            // Auto-assign sample ID if user leaves it blank
            activeTxnId = generateSampleTxnId(settlementForm.transferMode);
            setSettlementForm(prev => ({ ...prev, transactionId: activeTxnId }));
            toast.info(`Transaction ID was blank. Auto-assigned: ${activeTxnId}`);
        }
        if (!settlementForm.masterNo?.trim()) {
            toast.error("Please provide a Master Settlement Number");
            return;
        }
        if (settlementForm.transferMode !== 'UPI' && !settlementForm.accountNo?.trim()) {
            toast.error("Please provide Retailer Bank Account Number");
            return;
        }

        setIsSettling(true);
        try {
            const apiUrl = Base_Url + settlecod;
            const headers = {
                cus_id: cus_id || localStorage.getItem("cus_id") || "",
                token: token || localStorage.getItem("token") || "",
                "Content-Type": "application/json",
            };

            const ordersArray = settlementOrdersToProcess.map(o => ({
                order_id: String(o.order_id),
                awb_number: String(o.awb_number || "—"),
                consignee_name: String(o.consignee_name || "—"),
                destination: String(o.destination || "—"),
                amount: parseFloat(o.amount || 0).toFixed(2),
                net_amount: parseFloat(o.netAmount || 0).toFixed(2),
            }));

            const payload = {
                retailer_id: String(settlementForm.retailerId),
                remaining_amount: parseFloat(settlementForm.netAmount).toFixed(2),
                order_ids: ordersArray,
                settle_type: `Bank Transfer (${settlementForm.transferMode})`,
                tnx_id: activeTxnId,
                settle_id: settlementForm.masterNo,
            };

            try {
                await axios.post(apiUrl, payload, { headers });
            } catch (apiErr) {
                console.warn("settlecod API call warning:", apiErr);
            }

            const newBatch = {
                id: settlementForm.masterNo,
                settle_id: settlementForm.masterNo,
                tnx_id: activeTxnId,
                retailer_id: String(settlementForm.retailerId),
                retailer_name: settlementForm.retailerName,
                remaining_amount: parseFloat(settlementForm.netAmount).toFixed(2),
                amount: parseFloat(settlementForm.grossAmount).toFixed(2),
                fee_deduction: parseFloat(settlementForm.feeAmount).toFixed(2),
                status: "Processed",
                settle_type: `Bank (${settlementForm.transferMode})`,
                bank_name: settlementForm.bankName,
                account_no: settlementForm.accountNo,
                ifsc: settlementForm.ifscCode,
                account_holder: settlementForm.accountHolder,
                upi_id: settlementForm.upiId,
                orders: ordersArray,
                order_ids: ordersArray,
                order_id: JSON.stringify(ordersArray),
                orders_count: ordersArray.length,
                created_at: new Date().toISOString(),
            };

            // Persist locally for immediate persistence and sync across tabs
            try {
                const currentLocal = JSON.parse(localStorage.getItem("admin_settlement_batches") || "[]");
                localStorage.setItem("admin_settlement_batches", JSON.stringify([newBatch, ...currentLocal]));
            } catch (e) {
                console.error("Local storage error:", e);
            }

            // Prepend new batch to history
            setRemittances(prev => [newBatch, ...prev]);

            // Remove settled orders from Upcoming COD
            const settledIdSet = new Set(settlementOrdersToProcess.map(o => String(o.order_id)));
            setAllDeliveredOrders(prev => prev.filter(o => !settledIdSet.has(String(o.order_id))));

            // Clear checkboxes
            setSelectedOrderIds(prev => prev.filter(id => !settledIdSet.has(String(id))));

            // Close dialogs
            setSettlementModalOpen(false);
            setViewingOrder(null);

            // Switch to Previous COD History tab
            setActiveTab("settlements");

            toast.success(
                `Settlement Disbursed! Master No: ${newBatch.settle_id} | UTR: ${newBatch.tnx_id} (₹${parseFloat(newBatch.remaining_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })})`,
                { autoClose: 5000 }
            );
        } catch (error) {
            console.error("Settlement error:", error);
            toast.error("Failed to process settlement.");
        } finally {
            setIsSettling(false);
        }
    };

    const handleCreateSettlement = () => {
        if (selectedOrdersList.length > 0) {
            openSettlementFlow(selectedOrdersList);
        } else if (displayedOrders.length > 0) {
            openSettlementFlow(displayedOrders);
        } else {
            toast.info("No unsettled COD orders available to settle.");
        }
    };

    const handleSettleSingleOrder = (order) => {
        openSettlementFlow(order);
    };

    const handleView = (item) => {
        setData(item);
        setIsPopupOpen(true);
    };

    const handleClose = () => {
        setIsPopupOpen(false);
    };

    const handleProcess = async (item) => {
        try {
            const apiUrl = Base_Url + paymentProcess;
            const headers = {
                cus_id: cus_id || localStorage.getItem("cus_id") || "",
                token: token || localStorage.getItem("token") || "",
                "Content-Type": "application/json",
            };

            const payload = {
                retailer_id: item.retailer_id,
                settle_id: item.settle_id,
                status: "Process",
                tnx_id: item.tnx_id,
                amount: item.remaining_amount,
            };

            const response = await axios.post(apiUrl, payload, { headers });

            if (response.data?.status === "SUCCESS" || response.data?.status === "success" || response.status === 200) {
                toast.success(response.data?.message || "Payment processed successfully!");
                setRemittances(prev => prev.map(r => (r.settle_id === item.settle_id || r.id === item.id) ? { ...r, status: "Processed" } : r));
                try {
                    const currentLocal = JSON.parse(localStorage.getItem("admin_settlement_batches") || "[]");
                    const updated = currentLocal.map(r => (r.settle_id === item.settle_id || r.id === item.id) ? { ...r, status: "Processed" } : r);
                    localStorage.setItem("admin_settlement_batches", JSON.stringify(updated));
                } catch { }
                fetchRemittanceList();
            } else {
                toast.error(response.data?.message || "Failed to process payment");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong.");
        }
    };

    const getStatusBadgeClass = (status) => {
        const s = status?.toLowerCase();
        if (isDarkMode) {
            if (s === 'completed' || s === 'processed') {
                return 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40 shadow-[0_0_10px_rgba(16,185,129,0.2)]';
            }
            if (s === 'pending') {
                return 'bg-amber-500/10 text-amber-300 border-amber-500/40 shadow-[0_0_10px_rgba(245,158,11,0.2)]';
            }
            return 'bg-gray-500/10 text-gray-400 border-gray-600/40';
        }
        if (s === 'completed' || s === 'processed') {
            return 'bg-green-100 text-green-700';
        }
        if (s === 'pending') {
            return 'bg-yellow-100 text-yellow-700';
        }
        return 'bg-gray-100 text-gray-600';
    };

    const renderMobileCard = (item, index) => (
        <div
            key={item.id || index}
            className={`rounded-xl border overflow-hidden transition-all duration-200 ${isDarkMode
                ? 'bg-gradient-to-br from-[#0f172a] via-[#0b1220] to-[#0f172a] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.15)]'
                : 'bg-white border-gray-200 shadow-sm'
                }`}
        >
            <div className={`flex items-center justify-between gap-3 px-3 py-2 border-b ${isDarkMode
                ? 'bg-gradient-to-r from-indigo-600/20 via-purple-600/15 to-indigo-600/20 border-indigo-500/25'
                : 'bg-gray-50 border-gray-200'
                }`}>
                <div className="min-w-0 flex-1">
                    <p className={`text-[8px] uppercase tracking-wider mb-0.5 font-semibold ${isDarkMode ? 'text-indigo-300/80' : 'text-gray-400'}`}>
                        Settled ID
                    </p>
                    <p className={`text-[10px] font-mono font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {item.settle_id || '—'}
                    </p>
                </div>
                <div className="text-right shrink-0">
                    <p className={`text-[8px] uppercase tracking-wider mb-0.5 font-semibold ${isDarkMode ? 'text-indigo-300/80' : 'text-gray-400'}`}>
                        COD Amount
                    </p>
                    <p className={`text-[12px] font-bold ${isDarkMode ? 'text-emerald-400' : 'text-gray-900'}`}>
                        ₹{item.remaining_amount}
                    </p>
                </div>
            </div>

            <div className={`px-3 py-1.5 divide-y ${isDarkMode ? 'divide-indigo-500/15' : 'divide-gray-100'}`}>
                <div className="flex items-center justify-between py-1.5">
                    <span className={`text-[9px] font-medium uppercase tracking-wider ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>
                        Retailer ID
                    </span>
                    <span className={`text-[10px] font-semibold ${isDarkMode ? 'text-indigo-100' : 'text-gray-800'}`}>
                        {item.retailer_id || '—'}
                    </span>
                </div>

                <div className="flex items-center justify-between py-1.5 gap-3">
                    <span className={`text-[9px] font-medium uppercase tracking-wider shrink-0 ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>
                        Status
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold border capitalize ${getStatusBadgeClass(item.status)}`}>
                        <span className={`w-1 h-1 rounded-full mr-1 ${item.status?.toLowerCase() === 'completed' || item.status?.toLowerCase() === 'processed'
                            ? 'bg-emerald-400 animate-pulse'
                            : item.status?.toLowerCase() === 'pending'
                                ? 'bg-amber-400 animate-pulse'
                                : 'bg-gray-400'
                            }`}></span>
                        {item.status || '—'}
                    </span>
                </div>
            </div>

            <div className={`flex items-center justify-end gap-1.5 px-3 py-1.5 border-t ${isDarkMode ? 'border-indigo-500/15 bg-indigo-500/5' : 'border-gray-100 bg-gray-50'
                }`}>
                <button
                    onClick={() => handleView(item)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-[9px] font-medium transition-all duration-150 ${isDarkMode
                        ? 'border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                >
                    <Eye size={11} />
                    View
                </button>
                <button
                    onClick={() => handleProcess(item)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-[9px] font-medium transition-all duration-150 ${isDarkMode
                        ? 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                >
                    <FcProcess size={11} />
                    Process
                </button>
            </div>
        </div>
    );

    return (
        <>
            <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${isDarkMode ? 'dark bg-[#0B1120]' : 'bg-slate-50'}`}>
                <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />

                <NavBar />
                <div className="flex flex-1 overflow-hidden">
                    {isToggleSidebar && (
                        <div
                            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
                            onClick={() => setIsToggleSidebar(false)}
                        />
                    )}

                    <div
                        className={`
                            fixed inset-y-0 left-0 z-50 w-64
                            ${isToggleSidebar ? "translate-x-0" : "-translate-x-full"}
                            md:static md:inset-auto md:z-auto md:translate-x-0
                            ${isToggleSidebar ? "md:w-64" : "md:w-[72px]"}
                            m-2 rounded-2xl overflow-hidden transition-all duration-300 ease-in-out
                        `}
                    >
                        <Sidebar />
                    </div>

                    {/* Main Content Area - Optimized for maximum table space and natural fit */}
                    <div className={`flex flex-1 flex-col w-full overflow-y-auto custom-scrollbar p-2 transition-colors duration-300 min-w-0 ${isDarkMode ? 'bg-[#0B1120] text-gray-200' : 'bg-slate-50'}`}>

                        {/* Header: Compact single row with Integrated Date Filter Bar */}
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-1.5 mb-1.5 shrink-0">
                            <div className="flex items-center gap-2">
                                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-indigo-500/10 border border-indigo-500/30 text-indigo-400' : 'bg-red-50 border border-red-200 text-red-600'}`}>
                                    <FaRupeeSign className="w-4 h-4" />
                                </div>
                                <div>
                                    <h1 className={`text-lg font-bold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                        Remittance Dashboard
                                    </h1>
                                    <p className={`text-[9.5px] leading-none ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Manage COD settlements, upcoming payouts, and live delivered orders
                                    </p>
                                </div>
                            </div>

                            {/* Top Date Filter Ribbon */}
                            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-xl border text-[11px] shadow-2xs flex-wrap ${
                                isDarkMode ? 'bg-[#111827] border-gray-800' : 'bg-white border-gray-200'
                            }`}>
                                <Calendar size={13} className={isDarkMode ? 'text-indigo-400' : 'text-blue-600'} />
                                <span className={`text-[9px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Date:
                                </span>

                                {/* Quick Presets */}
                                <div className="flex items-center gap-0.5">
                                    {[
                                        { id: 'all', label: 'All' },
                                        { id: 'today', label: 'Today', title: 'Due Today (COD Payout)' },
                                        { id: 'tomorrow', label: 'Tomorrow', title: 'Due Tomorrow (COD Payout)' },
                                        { id: 'after_tomorrow', label: 'After Tomorrow', title: 'Due Day After Tomorrow (COD Payout)' },
                                        { id: 'yesterday', label: 'Yesterday', title: 'Yesterday' },
                                        { id: '7d', label: '7D', title: 'Last 7 Days' },
                                        { id: '30d', label: '30D', title: 'Last 30 Days' },
                                        { id: 'this_month', label: 'This Month', title: 'This Month' },
                                    ].map(p => (
                                        <button
                                            key={p.id}
                                            onClick={() => handlePresetChange(p.id)}
                                            title={p.title}
                                            className={`px-1.5 py-0.5 rounded text-[9.5px] font-semibold transition-all whitespace-nowrap ${
                                                datePreset === p.id
                                                    ? (isDarkMode ? 'bg-indigo-600 text-white shadow-2xs' : 'bg-blue-600 text-white shadow-2xs')
                                                    : (isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100')
                                            }`}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="h-3 w-[1px] bg-gray-300 dark:bg-gray-700 mx-0.5" />

                                {/* From / To Date Pickers */}
                                <div className="flex items-center gap-1">
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => {
                                            setStartDate(e.target.value);
                                            setDatePreset('custom');
                                            setOrderPage(1);
                                        }}
                                        className={`px-1.5 py-0.5 text-[10px] rounded border outline-none font-mono ${
                                            isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-800'
                                        }`}
                                        title="Start Date"
                                    />
                                    <span className={`text-[9px] ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>→</span>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => {
                                            setEndDate(e.target.value);
                                            setDatePreset('custom');
                                            setOrderPage(1);
                                        }}
                                        className={`px-1.5 py-0.5 text-[10px] rounded border outline-none font-mono ${
                                            isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-800'
                                        }`}
                                        title="End Date"
                                    />
                                    {(startDate || endDate) && (
                                        <button
                                            onClick={handleClearDateFilter}
                                            className="p-0.5 text-gray-400 hover:text-rose-500 rounded transition-colors"
                                            title="Clear Date Filter"
                                        >
                                            <X size={12} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ================= EXECUTIVE SUMMARY: UPCOMING COD vs PREVIOUS COD HISTORY ================= */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 mb-1.5 shrink-0">
                            {/* Card 1: Upcoming COD (Scheduled / Unsettled) */}
                            <div
                                onClick={() => setActiveTab('orders')}
                                className={`p-2 rounded-xl border cursor-pointer transition-all duration-200 relative overflow-hidden group ${
                                    activeTab === 'orders'
                                        ? isDarkMode
                                            ? 'bg-gradient-to-r from-indigo-950/40 via-blue-950/30 to-[#111827] border-indigo-500/60 shadow-[0_0_15px_rgba(99,102,241,0.15)] ring-1 ring-indigo-500/30'
                                            : 'bg-gradient-to-r from-indigo-50/90 via-blue-50/60 to-white border-indigo-300 shadow-xs ring-1 ring-indigo-200'
                                        : isDarkMode
                                            ? 'bg-[#111827] border-gray-800 hover:border-gray-700'
                                            : 'bg-white border-gray-200 hover:border-gray-300 shadow-2xs'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-1.5">
                                        <div className={`p-1 rounded-md ${isDarkMode ? 'bg-indigo-500/15 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                                            <Clock size={12} />
                                        </div>
                                        <div>
                                            <span className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>
                                                Upcoming COD (Next Payout)
                                            </span>
                                            {datePreset === 'tomorrow' && (
                                                <span className="ml-1.5 px-1.5 py-0.2 rounded-full text-[7.5px] font-bold bg-blue-500/15 text-blue-500 border border-blue-500/30">
                                                    Tomorrow Payout
                                                </span>
                                            )}
                                            {datePreset === 'after_tomorrow' && (
                                                <span className="ml-1.5 px-1.5 py-0.2 rounded-full text-[7.5px] font-bold bg-purple-500/15 text-purple-500 border border-purple-500/30">
                                                    After Tomorrow Payout
                                                </span>
                                            )}
                                            {datePreset === 'today' && (
                                                <span className="ml-1.5 px-1.5 py-0.2 rounded-full text-[7.5px] font-bold bg-amber-500/15 text-amber-500 border border-amber-500/30">
                                                    Today Payout
                                                </span>
                                            )}
                                            {(datePreset === 'all' || datePreset === 'custom' || datePreset === 'yesterday' || datePreset === '7d' || datePreset === '30d' || datePreset === 'this_month') && (
                                                <span className="ml-1.5 px-1.5 py-0.2 rounded-full text-[7.5px] font-bold bg-amber-500/15 text-amber-500 border border-amber-500/30">
                                                    Awaiting Settlement
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <span className={`text-[9px] font-bold flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform ${
                                        activeTab === 'orders' ? (isDarkMode ? 'text-indigo-400' : 'text-indigo-600') : 'text-gray-400'
                                    }`}>
                                        View Orders ({displayedOrders.length}) →
                                    </span>
                                </div>

                                <div className="flex items-baseline justify-between">
                                    <div className="flex items-baseline gap-1.5">
                                        <span className={`text-base font-black tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                            ₹{upcomingSummary.net.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </span>
                                        <span className={`text-[9.5px] font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Net Payable
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <span className={`text-[10px] font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                            Gross: ₹{upcomingSummary.gross.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </span>
                                        <span className={`text-[8.5px] block ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Fee: {upcomingSummary.feeAmount > 0 ? `-₹${upcomingSummary.feeAmount.toFixed(2)}` : '₹0.00 (0% Weekly Default)'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Card 2: Previous COD History (Disbursed Settlements) */}
                            <div
                                onClick={() => setActiveTab('settlements')}
                                className={`p-2 rounded-xl border cursor-pointer transition-all duration-200 relative overflow-hidden group ${
                                    activeTab === 'settlements'
                                        ? isDarkMode
                                            ? 'bg-gradient-to-r from-emerald-950/40 via-teal-950/30 to-[#111827] border-emerald-500/60 shadow-[0_0_15px_rgba(16,185,129,0.15)] ring-1 ring-emerald-500/30'
                                            : 'bg-gradient-to-r from-emerald-50/90 via-teal-50/60 to-white border-emerald-300 shadow-xs ring-1 ring-emerald-200'
                                        : isDarkMode
                                            ? 'bg-[#111827] border-gray-800 hover:border-gray-700'
                                            : 'bg-white border-gray-200 hover:border-gray-300 shadow-2xs'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-1.5">
                                        <div className={`p-1 rounded-md ${isDarkMode ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
                                            <CheckCircle2 size={12} />
                                        </div>
                                        <div>
                                            <span className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                                                Previous COD History
                                            </span>
                                            <span className="ml-1.5 px-1.5 py-0.2 rounded-full text-[7.5px] font-bold bg-emerald-500/15 text-emerald-500 border border-emerald-500/30">
                                                Disbursed &amp; Settled
                                            </span>
                                        </div>
                                    </div>
                                    <span className={`text-[9px] font-bold flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform ${
                                        activeTab === 'settlements' ? (isDarkMode ? 'text-emerald-400' : 'text-emerald-600') : 'text-gray-400'
                                    }`}>
                                        View History ({filteredRemittances.length}) →
                                    </span>
                                </div>

                                <div className="flex items-baseline justify-between">
                                    <div className="flex items-baseline gap-1.5">
                                        <span className={`text-base font-black tracking-tight ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                            ₹{previousHistorySummary.totalSettled.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </span>
                                        <span className={`text-[9.5px] font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Total Settled
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <span className={`text-[10px] font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                            {previousHistorySummary.totalBatches} Batches ({previousHistorySummary.processedCount} Processed)
                                        </span>
                                        <span className={`text-[8.5px] block ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            Last Settlement: {previousHistorySummary.latestSettledDate}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* COMPACT Payout Cards Ribbon (Horizontal Slim Cards - 5 Cards including Weekly Default) */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5 mb-1.5 shrink-0">
                            {payoutCards.map((card) => {
                                const isCurrentActive = Boolean(
                                    (card.id === 'Weekly' && (!retailerActivePlan || retailerActivePlan === 'Weekly')) ||
                                    (retailerActivePlan === card.id)
                                );
                                return (
                                    <div
                                        key={card.id}
                                        className={`relative flex items-center justify-between px-2.5 py-1.5 rounded-lg border transition-all duration-150 ${isCurrentActive
                                            ? isDarkMode
                                                ? 'bg-emerald-950/25 border-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.2)] ring-1 ring-emerald-500/30'
                                                : 'bg-emerald-50/80 border-emerald-500 shadow-xs ring-1 ring-emerald-300'
                                            : isDarkMode
                                                ? 'bg-[#131b2e] border-gray-800/90 hover:border-gray-700'
                                                : 'bg-white border-gray-200 hover:border-gray-300 shadow-2xs'
                                            }`}
                                    >
                                        <div className="min-w-0 pr-2">
                                            <div className="flex items-center gap-1.5">
                                                <span className={`text-[10px] font-bold truncate ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                                    {card.title}
                                                </span>
                                                {isCurrentActive && (
                                                    <span className="px-1.5 py-0.2 rounded-full text-[7.5px] font-bold bg-emerald-500 text-white flex items-center gap-0.5 shrink-0">
                                                        <CheckCircle2 size={8} /> Active
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className={`text-[11px] font-extrabold ${isCurrentActive ? 'text-emerald-500' : (isDarkMode ? 'text-emerald-400' : 'text-emerald-600')}`}>
                                                    {card.percentage}
                                                </span>
                                                <span className={`text-[8.5px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    ({card.frequency})
                                                </span>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => !isCurrentActive && handleActivatePayout(card.id)}
                                            disabled={!selectedCustomerId || activatingCardId === card.id || isCurrentActive}
                                            className={`shrink-0 px-2 py-0.5 rounded text-[9px] font-bold transition-all ${isCurrentActive
                                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 cursor-default'
                                                : !selectedCustomerId
                                                    ? 'bg-gray-200 dark:bg-gray-800 text-gray-400 cursor-not-allowed text-[8px]'
                                                    : isDarkMode
                                                        ? 'bg-indigo-600 hover:bg-indigo-500 text-white cursor-pointer shadow-xs'
                                                        : 'bg-red-600 hover:bg-red-700 text-white cursor-pointer shadow-xs'
                                                }`}
                                        >
                                            {activatingCardId === card.id ? <Spinner /> : isCurrentActive ? '✓ Active' : (card.id === 'Weekly' ? 'Revert (0%)' : 'Activate')}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* COMPACT Retailer Command Bar (Selector + Live Stats + Actions in 1 Row) */}
                        <div className={`p-1.5 rounded-xl border mb-1.5 shrink-0 transition-all ${isDarkMode
                            ? 'bg-[#111827] border-gray-800 shadow-sm'
                            : 'bg-white border-gray-200 shadow-2xs'
                            }`}>
                            <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-2">
                                {/* Retailer Dropdown (with 'All Retailers' option) */}
                                <div className="w-full lg:w-72 shrink-0">
                                    <Select
                                        options={selectCustomerOptions}
                                        onChange={handleSelectChangeId}
                                        value={selectCustomerOptions.find(opt => opt.value === (selectedCustomerId || "ALL")) || selectCustomerOptions[0]}
                                        isClearable={false}
                                        placeholder="Filter by Retailer..."
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                padding: "0 0.2rem",
                                                borderRadius: "0.5rem",
                                                minHeight: "30px",
                                                height: "30px",
                                                fontSize: "11px",
                                                backgroundColor: isDarkMode ? "#1F2937" : "#ffffff",
                                                borderColor: isDarkMode ? "#374151" : "#d1d5db",
                                                color: isDarkMode ? "white" : "#1f2937",
                                                boxShadow: 'none',
                                                '&:hover': {
                                                    borderColor: isDarkMode ? '#6366f1' : '#9ca3af',
                                                },
                                            }),
                                            valueContainer: (base) => ({
                                                ...base,
                                                padding: '0 4px',
                                            }),
                                            singleValue: (base) => ({
                                                ...base,
                                                color: isDarkMode ? "white" : "#1f2937",
                                                fontSize: "11px",
                                                fontWeight: 600,
                                            }),
                                            placeholder: (base) => ({
                                                ...base,
                                                fontSize: "11px",
                                                color: isDarkMode ? "#9ca3af" : "#6b7280",
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                backgroundColor: isDarkMode ? "#1F2937" : "#ffffff",
                                                border: isDarkMode ? "1px solid #374151" : "1px solid #d1d5db",
                                                fontSize: "11px",
                                                zIndex: 50,
                                            }),
                                            option: (base, state) => ({
                                                ...base,
                                                backgroundColor: state.isFocused
                                                    ? (isDarkMode ? "#111827" : "#f3f4f6")
                                                    : (isDarkMode ? "#1F2937" : "#ffffff"),
                                                color: isDarkMode ? "white" : "#1f2937",
                                                fontSize: "11px",
                                                cursor: "pointer",
                                                padding: "6px 10px",
                                            }),
                                        }}
                                    />
                                </div>

                                {/* Inline Stats Pills - ALWAYS VISIBLE */}
                                <div className="flex items-center gap-1.5 flex-wrap flex-1 justify-center lg:justify-start">
                                    <div className={`px-2.5 py-1 rounded-md border flex items-center gap-1.5 ${isDarkMode ? 'bg-black/30 border-gray-800' : 'bg-slate-50 border-gray-200'
                                        }`}>
                                        <span className={`text-[8.5px] uppercase font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Gross:</span>
                                        <span className={`text-[11px] font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                            ₹{remittanceSummary.gross.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>

                                    <div className={`px-2.5 py-1 rounded-md border flex items-center gap-1.5 ${isDarkMode ? 'bg-black/30 border-gray-800' : 'bg-slate-50 border-gray-200'
                                        }`}>
                                        <span className={`text-[8.5px] uppercase font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Fee ({remittanceSummary.feePercent}%):</span>
                                        <span className={`text-[11px] font-bold ${remittanceSummary.feeAmount > 0 ? (isDarkMode ? 'text-amber-400' : 'text-amber-600') : (isDarkMode ? 'text-emerald-400' : 'text-emerald-600')}`}>
                                            {remittanceSummary.feeAmount > 0 ? `-₹${remittanceSummary.feeAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '₹0.00 (No Charge)'}
                                        </span>
                                    </div>

                                    <div className={`px-2.5 py-1 rounded-md border flex items-center gap-1.5 ${isDarkMode ? 'bg-indigo-950/40 border-indigo-500/30' : 'bg-indigo-50/70 border-indigo-200'
                                        }`}>
                                        <span className={`text-[8.5px] uppercase font-semibold ${isDarkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>Net Payout:</span>
                                        <span className={`text-[11px] font-extrabold ${isDarkMode ? 'text-indigo-200' : 'text-indigo-700'}`}>
                                            ₹{remittanceSummary.net.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>

                                    <div className={`px-2 py-1 rounded-md border flex items-center gap-1 ${isDarkMode ? 'bg-black/30 border-gray-800 text-gray-300' : 'bg-slate-50 border-gray-200 text-gray-700'
                                        }`}>
                                        <span className="text-[10px] font-bold">{displayedOrders.length}</span>
                                        <span className="text-[8.5px] uppercase font-medium">Orders</span>
                                    </div>
                                </div>

                                {/* Action buttons */}
                                <div className="flex items-center gap-1.5 justify-end shrink-0">
                                    <button
                                        onClick={handleExportRetailerOrders}
                                        disabled={displayedOrders.length === 0}
                                        className={`flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold rounded-md border transition-all ${displayedOrders.length === 0
                                            ? 'opacity-40 cursor-not-allowed bg-gray-200 dark:bg-gray-800 text-gray-500'
                                            : isDarkMode
                                                ? 'border-indigo-500/40 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20'
                                                : 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
                                            }`}
                                        title="Export CSV"
                                    >
                                        <Download size={11} />
                                        <span>CSV</span>
                                    </button>
                                    <button
                                        onClick={handleCreateSettlement}
                                        disabled={isSettling}
                                        className={`flex items-center gap-1 px-3 py-1 text-[10px] font-bold rounded-md text-white transition-all shadow-xs ${isSettling
                                            ? 'opacity-50 cursor-not-allowed bg-gray-500'
                                            : !selectedCustomerId || selectedCustomerId === "ALL"
                                                ? isDarkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 cursor-pointer' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 cursor-pointer'
                                                : isDarkMode
                                                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 cursor-pointer'
                                                    : 'bg-emerald-600 hover:bg-emerald-700 cursor-pointer'
                                            }`}
                                        title={!selectedCustomerId || selectedCustomerId === "ALL" ? "Select a retailer to create settlement batch" : "Create Settlement Batch"}
                                    >
                                        {isSettling ? <Spinner /> : <CheckCircle2 size={11} />}
                                        <span>Create Settlement</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Tabs & Search Bar Strip */}
                        <div className="flex items-center justify-between mb-1.5 shrink-0 gap-2 flex-wrap">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <button
                                    onClick={() => setActiveTab('orders')}
                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all ${activeTab === 'orders'
                                        ? (isDarkMode ? 'bg-indigo-600 text-white shadow-xs' : 'bg-red-600 text-white shadow-xs')
                                        : (isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-800/60' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200')
                                        }`}
                                >
                                    <Clock size={12} />
                                    <span>Upcoming COD</span>
                                    <span className={`px-1.5 py-0.2 rounded-full text-[8.5px] font-bold ${activeTab === 'orders' ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                        }`}>
                                        {displayedOrders.length}
                                    </span>
                                </button>

                                <button
                                    onClick={() => setActiveTab('settlements')}
                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all ${activeTab === 'settlements'
                                        ? (isDarkMode ? 'bg-indigo-600 text-white shadow-xs' : 'bg-red-600 text-white shadow-xs')
                                        : (isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-800/60' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200')
                                        }`}
                                >
                                    <History size={12} />
                                    <span>Previous COD History</span>
                                    <span className={`px-1.5 py-0.2 rounded-full text-[8.5px] font-bold ${activeTab === 'settlements' ? 'bg-white/20 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                        }`}>
                                        {filteredRemittances.length}
                                    </span>
                                </button>
                            </div>

                            <div className="relative min-w-[240px]">
                                <Search size={11} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                                {activeTab === 'orders' ? (
                                    <input
                                        type="text"
                                        placeholder="Search Order ID, AWB, User, Customer..."
                                        value={orderSearchTerm}
                                        onChange={(e) => {
                                            setOrderSearchTerm(e.target.value);
                                            setOrderPage(1);
                                        }}
                                        className={`w-full pl-7 pr-3 py-1 text-[11px] rounded-lg border outline-none transition-all ${isDarkMode
                                            ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-indigo-500'
                                            : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-blue-500'
                                            }`}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        placeholder="Search Settled ID, Retailer, Status..."
                                        value={settlementSearchTerm}
                                        onChange={(e) => setSettlementSearchTerm(e.target.value)}
                                        className={`w-full pl-7 pr-3 py-1 text-[11px] rounded-lg border outline-none transition-all ${isDarkMode
                                            ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-indigo-500'
                                            : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-blue-500'
                                            }`}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Active Selection Banner for Settlement */}
                        {activeTab === 'orders' && selectedOrderIds.length > 0 && (
                            <div className={`flex items-center justify-between px-3 py-1.5 mb-1.5 rounded-xl border text-xs transition-all ${
                                isDarkMode
                                    ? 'bg-gradient-to-r from-indigo-950/80 via-purple-950/60 to-indigo-950/80 border-indigo-500/50 text-indigo-100 shadow-[0_0_15px_rgba(99,102,241,0.2)]'
                                    : 'bg-gradient-to-r from-indigo-50 via-purple-50 to-indigo-50 border-indigo-200 text-indigo-900 shadow-sm'
                            }`}>
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <div className="flex items-center gap-1.5 font-bold">
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                                            isDarkMode ? 'bg-indigo-500/30 text-indigo-200 border border-indigo-400/40' : 'bg-indigo-600 text-white'
                                        }`}>
                                            {selectedOrdersSummary.count} Selected
                                        </span>
                                        <span className="text-[11px]">Orders Marked for Settlement</span>
                                    </div>
                                    <div className="h-3.5 w-[1px] bg-indigo-300 dark:bg-indigo-700 hidden sm:block" />
                                    <div className="flex items-center gap-2 text-[11px]">
                                        <span className="opacity-75">Gross COD:</span>
                                        <span className="font-bold">₹{selectedOrdersSummary.gross.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                        <span className="opacity-50">•</span>
                                        <span className="opacity-75">Deductions:</span>
                                        <span className={`font-semibold ${selectedOrdersSummary.fee > 0 ? (isDarkMode ? 'text-amber-400' : 'text-amber-600') : ''}`}>
                                            -₹{selectedOrdersSummary.fee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </span>
                                        <span className="opacity-50">•</span>
                                        <span className="opacity-75">Net Payable:</span>
                                        <span className="font-extrabold text-emerald-500 text-xs">
                                            ₹{selectedOrdersSummary.net.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setSelectedOrderIds([])}
                                        className={`px-2 py-1 rounded text-[10.5px] font-medium transition-all ${
                                            isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                                        }`}
                                    >
                                        Clear Selection
                                    </button>
                                    <button
                                        onClick={() => openSettlementFlow(selectedOrdersList)}
                                        disabled={isSettling}
                                        className="flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-sm cursor-pointer transition-all"
                                    >
                                        <Zap size={12} />
                                        <span>Disburse &amp; Settle Selected (₹{selectedOrdersSummary.net.toLocaleString('en-IN', { minimumFractionDigits: 2 })})</span>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ================= TABLE AREA: Maximized Height with flex-1 ================= */}
                        <div className={`flex-1 min-h-0 flex flex-col rounded-xl border overflow-hidden transition-colors ${isDarkMode
                            ? 'border-gray-800 bg-[#111827] shadow-lg'
                            : 'border-gray-200 bg-white shadow-xs'
                            }`}>
                            {activeTab === 'orders' ? (
                                <div className="flex-1 overflow-auto custom-scrollbar">
                                    <table className="w-full text-left border-collapse">
                                        <thead className={`sticky top-0 z-10 transition-colors ${isDarkMode
                                            ? 'bg-[#182235] border-b border-gray-700/80 text-gray-300'
                                            : 'bg-gray-100 border-b border-gray-200 text-gray-600'
                                            }`}>
                                            <tr>
                                                <th className="py-1.5 px-2 text-center w-8">
                                                    <input
                                                        type="checkbox"
                                                        checked={isAllFilteredSelected}
                                                        ref={input => {
                                                            if (input) {
                                                                input.indeterminate = isSomeFilteredSelected;
                                                            }
                                                        }}
                                                        onChange={handleToggleSelectAll}
                                                        className="w-3.5 h-3.5 rounded border-gray-400 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                                        title={isAllFilteredSelected ? "Deselect All" : "Select All Filtered"}
                                                    />
                                                </th>
                                                <th className="py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap">Order ID &amp; AWB</th>
                                                <th className="py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap">Retailer / User</th>
                                                <th className="py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap">Consignee Info</th>
                                                <th className="py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap">Origin &amp; Destination</th>
                                                <th className="py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider text-right whitespace-nowrap">Gross COD</th>
                                                <th className="py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider text-right whitespace-nowrap">
                                                    <div>Net Payout</div>
                                                    <div className="text-[7px] font-normal lowercase tracking-normal opacity-70">post deduction</div>
                                                </th>
                                                <th className="py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider text-center whitespace-nowrap">Status</th>
                                                <th className="py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider text-center whitespace-nowrap">Date</th>
                                                <th className="py-1.5 px-2 text-[9px] font-bold uppercase tracking-wider text-center whitespace-nowrap w-24">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800/80' : 'divide-gray-100'}`}>
                                            {ordersLoading ? (
                                                <tr>
                                                    <td colSpan={10} className="py-12 text-center">
                                                        <div className="flex flex-col justify-center items-center gap-2">
                                                            <Spinner />
                                                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                Fetching live delivered COD orders...
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : paginatedOrders.length === 0 ? (
                                                <tr>
                                                    <td colSpan={10} className={`py-12 text-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                        {orderSearchTerm
                                                            ? `No orders matching "${orderSearchTerm}"`
                                                            : selectedCustomerId && selectedCustomerId !== "ALL"
                                                                ? `No delivered COD orders found for Retailer ID: ${selectedCustomerId}`
                                                                : "No delivered COD orders found"}
                                                    </td>
                                                </tr>
                                            ) : (
                                                paginatedOrders.map((order, idx) => {
                                                    const isSelected = selectedOrderIds.includes(String(order.order_id));
                                                    const rawDate = order.order_date;
                                                    const formattedDate = rawDate && rawDate !== "—"
                                                        ? new Date(rawDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                                        : "—";
                                                    const formattedTime = rawDate && rawDate !== "—"
                                                        ? new Date(rawDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
                                                        : "";

                                                    return (
                                                        <tr
                                                            key={order.order_id || idx}
                                                            className={`transition-colors duration-100 ${
                                                                isSelected
                                                                    ? isDarkMode ? 'bg-indigo-950/40 border-l-2 border-indigo-500' : 'bg-indigo-50/80 border-l-2 border-indigo-500'
                                                                    : isDarkMode
                                                                        ? idx % 2 === 0 ? 'bg-[#111827] hover:bg-[#1a2336]' : 'bg-[#0e1422] hover:bg-[#1a2336]'
                                                                        : idx % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-50'
                                                            }`}
                                                        >
                                                            {/* 0. Row Checkbox */}
                                                            <td className="py-1.5 px-2 text-center align-middle w-8">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    onChange={() => handleToggleRow(order.order_id)}
                                                                    className="w-3.5 h-3.5 rounded border-gray-400 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                                                />
                                                            </td>

                                                            {/* 1. Order ID & AWB */}
                                                            <td className="py-1.5 px-2.5 align-middle whitespace-nowrap">
                                                                <div className="flex flex-col">
                                                                    <div className="flex items-center gap-1">
                                                                        <span
                                                                            onClick={() => copyToClipboard(order.order_id, "Order ID")}
                                                                            className={`text-[11px] font-bold font-mono hover:underline cursor-pointer ${isDarkMode ? 'text-indigo-400' : 'text-blue-600'
                                                                                }`}
                                                                        >
                                                                            {order.order_id}
                                                                        </span>
                                                                        <Copy
                                                                            size={9}
                                                                            className="cursor-pointer text-gray-400 hover:text-gray-200"
                                                                            onClick={() => copyToClipboard(order.order_id, "Order ID")}
                                                                        />
                                                                    </div>
                                                                    {order.awb_number && order.awb_number !== "—" ? (
                                                                        <span
                                                                            onClick={() => copyToClipboard(order.awb_number, "AWB")}
                                                                            className={`text-[9.5px] font-mono cursor-pointer hover:underline inline-flex items-center gap-1 whitespace-nowrap mt-0.5 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                                                                                }`}
                                                                            title="Click to copy AWB"
                                                                        >
                                                                            <span>AWB:</span>
                                                                            <span className="font-semibold">{order.awb_number}</span>
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-[9px] text-gray-400 whitespace-nowrap mt-0.5">AWB: —</span>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* 2. Retailer / User ID */}
                                                            <td className="py-1.5 px-2 align-middle whitespace-nowrap">
                                                                <div className="flex flex-col">
                                                                    <div className="flex items-center gap-1">
                                                                        <span className={`inline-flex items-center px-1.5 py-0.2 rounded text-[9.5px] font-bold font-mono whitespace-nowrap ${isDarkMode
                                                                            ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30'
                                                                            : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                                                            }`}>
                                                                            ID: {order.user_id}
                                                                        </span>
                                                                    </div>
                                                                    {customerMap[order.user_id] ? (
                                                                        <span className={`text-[9.5px] font-semibold truncate max-w-[120px] mt-0.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-800'
                                                                            }`}>
                                                                            {customerMap[order.user_id]}
                                                                        </span>
                                                                    ) : order.user_id !== "—" ? (
                                                                        <span className="text-[8.5px] text-gray-400 mt-0.5 whitespace-nowrap">
                                                                            Retailer #{order.user_id}
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                            </td>

                                                            {/* 3. Customer Info */}
                                                            <td className="py-1.5 px-2 align-middle whitespace-nowrap">
                                                                <div className="flex items-center gap-1.5">
                                                                    <div className="w-5 h-5 rounded-full bg-gradient-to-tr from-pink-500 via-purple-500 to-indigo-500 text-white text-[8.5px] font-bold flex items-center justify-center shrink-0 shadow-xs">
                                                                        {(order.consignee_name?.charAt(0) || "C").toUpperCase()}
                                                                    </div>
                                                                    <div className="flex flex-col min-w-0">
                                                                        <span className={`text-[10.5px] font-semibold truncate capitalize max-w-[120px] ${isDarkMode ? 'text-gray-100' : 'text-gray-900'
                                                                            }`}>
                                                                            {order.consignee_name}
                                                                        </span>
                                                                        <span className={`text-[9px] font-mono whitespace-nowrap ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                            {order.consignee_phone}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* 4. Origin & Destination */}
                                                            <td className="py-1.5 px-2 align-middle whitespace-nowrap">
                                                                <div className="flex items-center gap-1 text-[10.5px] whitespace-nowrap">
                                                                    <span className={`font-medium capitalize ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                                                        {order.origin}
                                                                    </span>
                                                                    <ArrowRight size={10} className={`${isDarkMode ? 'text-indigo-400' : 'text-gray-400'}`} />
                                                                    <span className={`font-bold capitalize ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                                                                        {order.destination}
                                                                    </span>
                                                                </div>
                                                            </td>

                                                            {/* 5. Gross COD / Payment */}
                                                            <td className="py-1.5 px-2 text-right align-middle whitespace-nowrap">
                                                                <div className="flex flex-col items-end">
                                                                    <span className={`text-[11px] font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                                                        ₹{parseFloat(order.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                                    </span>
                                                                    <span className={`text-[7.5px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                        Gross COD
                                                                    </span>
                                                                </div>
                                                            </td>

                                                            {/* 6. Net Payout */}
                                                            <td className="py-1.5 px-2 text-right align-middle whitespace-nowrap">
                                                                <div className="flex flex-col items-end">
                                                                    <span className={`text-[11.5px] font-extrabold whitespace-nowrap ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                                                        ₹{parseFloat(order.netAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                                    </span>
                                                                    <div className="flex items-center gap-1 mt-0.5 whitespace-nowrap">
                                                                        <span className={`text-[8px] px-1 py-0.2 rounded font-semibold whitespace-nowrap inline-block ${order.feePercent > 0
                                                                            ? (isDarkMode ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/30' : 'bg-indigo-50 text-indigo-700 border border-indigo-200')
                                                                            : (isDarkMode ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border border-emerald-200')
                                                                            }`}>
                                                                            {order.feePercent > 0 ? `${order.userPlan} (${order.feePercent}%)` : `Weekly (0%)`}
                                                                        </span>
                                                                        {order.feePercent > 0 ? (
                                                                            <span className={`text-[8px] font-semibold whitespace-nowrap ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`}>
                                                                                -₹{parseFloat(order.feeAmount || 0).toFixed(2)}
                                                                            </span>
                                                                        ) : (
                                                                            <span className={`text-[8px] font-semibold whitespace-nowrap ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                                                                No Fee
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* 7. Status */}
                                                            <td className="py-1.5 px-2 text-center align-middle whitespace-nowrap">
                                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[8.5px] font-bold whitespace-nowrap ${
                                                                    isDarkMode
                                                                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                                                                        : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                                                }`}>
                                                                    <span className="w-1.5 h-1.5 rounded-full mr-1 bg-emerald-400 shrink-0 animate-pulse"></span>
                                                                    <span className="whitespace-nowrap">{order.status || 'Delivered'}</span>
                                                                </span>
                                                            </td>

                                                            {/* 8. Date */}
                                                            <td className="py-1.5 px-2 text-center align-middle whitespace-nowrap">
                                                                <div className="flex flex-col items-center gap-0.5">
                                                                    {order.payoutSchedule && (
                                                                        <span
                                                                            className={`inline-flex items-center px-1.5 py-0.2 rounded text-[7.5px] font-bold border tracking-tight shadow-2xs ${
                                                                                order.payoutSchedule.dueType === 'today'
                                                                                    ? (isDarkMode ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' : 'bg-amber-50 text-amber-700 border-amber-200')
                                                                                    : order.payoutSchedule.dueType === 'tomorrow'
                                                                                        ? (isDarkMode ? 'bg-blue-500/15 text-blue-300 border-blue-500/30' : 'bg-blue-50 text-blue-700 border-blue-200')
                                                                                        : order.payoutSchedule.dueType === 'after_tomorrow'
                                                                                            ? (isDarkMode ? 'bg-purple-500/15 text-purple-300 border-purple-500/30' : 'bg-purple-50 text-purple-700 border-purple-200')
                                                                                            : (isDarkMode ? 'bg-gray-800 text-gray-300 border-gray-700' : 'bg-gray-100 text-gray-600 border-gray-200')
                                                                            }`}
                                                                            title={`COD Remittance Due: ${order.payoutSchedule.subText}`}
                                                                        >
                                                                            <span
                                                                                className={`w-1 h-1 rounded-full mr-1 ${
                                                                                    order.payoutSchedule.dueType === 'today'
                                                                                        ? 'bg-amber-500 animate-pulse'
                                                                                        : order.payoutSchedule.dueType === 'tomorrow'
                                                                                            ? 'bg-blue-500'
                                                                                            : order.payoutSchedule.dueType === 'after_tomorrow'
                                                                                                ? 'bg-purple-500'
                                                                                                : 'bg-gray-400'
                                                                                }`}
                                                                            />
                                                                            {order.payoutSchedule.label}
                                                                        </span>
                                                                    )}
                                                                    <div className="flex flex-col items-center">
                                                                        <span className={`text-[9.5px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                                                            {formattedDate}
                                                                        </span>
                                                                        {formattedTime && (
                                                                            <span className={`text-[8px] ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                                                                                {formattedTime}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* 9. Action Column */}
                                                            <td className="py-1.5 px-2 text-center align-middle whitespace-nowrap w-24">
                                                                <div className="flex items-center justify-center gap-1">
                                                                    <button
                                                                        onClick={() => openSettlementFlow(order)}
                                                                        disabled={isSettling}
                                                                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold transition-all shadow-2xs ${
                                                                            isDarkMode
                                                                                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                                                                                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                                                        }`}
                                                                        title="Settle this order now"
                                                                    >
                                                                        <Zap size={9} />
                                                                        <span>Settle</span>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setViewingOrder(order)}
                                                                        className={`p-1 rounded border transition-all ${
                                                                            isDarkMode
                                                                                ? 'border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10'
                                                                                : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                                                        }`}
                                                                        title="View Order Details"
                                                                    >
                                                                        <Eye size={11} />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                /* TAB 2: Settlement History */
                                <div className="flex-1 overflow-auto custom-scrollbar">
                                    {/* Mobile/Tablet Card View */}
                                    <div className="lg:hidden p-2 space-y-2">
                                        {loading ? (
                                            <div className="flex justify-center items-center py-8">
                                                <Spinner />
                                            </div>
                                        ) : filteredRemittances.length === 0 ? (
                                            <div className={`text-center py-8 px-4 rounded-xl border ${isDarkMode ? 'bg-gray-900/40 border-gray-800' : 'bg-white border-gray-200'}`}>
                                                <History size={20} className="mx-auto mb-1.5 text-gray-400" />
                                                <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                                    {settlementSearchTerm
                                                        ? `No settlements matching "${settlementSearchTerm}"`
                                                        : selectedCustomerId && selectedCustomerId !== "ALL"
                                                            ? `No settlement records for Retailer ID: ${selectedCustomerId}`
                                                            : "No Settlement Records Found"}
                                                </p>
                                                <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    {selectedCustomerId && selectedCustomerId !== "ALL"
                                                        ? `This retailer has ${displayedOrders.length} delivered COD orders ready.`
                                                        : "You can create settlement batches from the Delivered COD Orders tab."}
                                                </p>
                                                <button
                                                    onClick={() => setActiveTab('orders')}
                                                    className="mt-2.5 inline-flex items-center gap-1 px-3 py-1 rounded-md text-[10px] font-bold bg-indigo-600 text-white hover:bg-indigo-500 transition-all"
                                                >
                                                    <PackageCheck size={11} />
                                                    <span>View Delivered COD Orders</span>
                                                </button>
                                            </div>
                                        ) : (
                                            filteredRemittances.map((item, index) => renderMobileCard(item, index))
                                        )}
                                    </div>

                                    {/* Desktop Table */}
                                    <table className="hidden lg:table w-full text-left border-collapse">
                                        <thead className={`sticky top-0 z-10 transition-colors ${isDarkMode
                                            ? 'bg-[#182235] border-b border-gray-700/80 text-gray-300'
                                            : 'bg-gray-100 border-b border-gray-200 text-gray-600'
                                            }`}>
                                            <tr>
                                                <th className="py-2 px-3 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap">Master No &amp; UTR</th>
                                                <th className="py-2 px-3 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap">Retailer &amp; Bank Info</th>
                                                <th className="py-2 px-3 text-[9px] font-bold uppercase tracking-wider whitespace-nowrap text-center">Status</th>
                                                <th className="py-2 px-3 text-[9px] font-bold uppercase tracking-wider text-right whitespace-nowrap">Settlement Amount</th>
                                                <th className="py-2 px-3 text-[9px] font-bold uppercase tracking-wider text-center whitespace-nowrap">Date</th>
                                                <th className="py-2 px-3 text-[9px] font-bold uppercase tracking-wider text-center whitespace-nowrap">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800/80' : 'divide-gray-100'}`}>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={6} className="py-10 text-center">
                                                        <div className="flex justify-center items-center">
                                                            <Spinner />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : filteredRemittances.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} className="py-12 text-center">
                                                        <div className="flex flex-col items-center justify-center gap-1.5">
                                                            <div className={`p-2 rounded-full ${isDarkMode ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>
                                                                <History size={20} />
                                                            </div>
                                                            <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                                                {settlementSearchTerm
                                                                    ? `No settlements matching "${settlementSearchTerm}"`
                                                                    : selectedCustomerId && selectedCustomerId !== "ALL"
                                                                        ? `No settlement records found for Retailer ID: ${selectedCustomerId}`
                                                                        : "No Settlement Records Found"}
                                                            </p>
                                                            <p className={`text-[10.5px] max-w-md ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                {selectedCustomerId && selectedCustomerId !== "ALL"
                                                                    ? `This retailer has ${displayedOrders.length} delivered COD orders ready. Switch to Delivered COD Orders to create a settlement batch.`
                                                                    : "No settlements have been processed yet. You can create batches from the Delivered COD Orders tab."}
                                                            </p>
                                                            <button
                                                                onClick={() => setActiveTab('orders')}
                                                                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[10.5px] font-bold bg-indigo-600 text-white hover:bg-indigo-500 transition-all shadow-xs"
                                                            >
                                                                <PackageCheck size={12} />
                                                                <span>View Delivered COD Orders ({displayedOrders.length})</span>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredRemittances.map((item, index) => {
                                                    const retId = item.retailer_id ?? item.user_id ?? item.cus_id ?? '—';
                                                    const retName = item.retailer_name || customerMap[String(retId)] || `Retailer ${retId}`;
                                                    const rawDate = item.created_at || item.updated_at || item.date || item.settle_date;
                                                    const formattedDate = rawDate && rawDate !== "—"
                                                        ? new Date(rawDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                                        : "—";
                                                    const userBank = rawUsersMap[String(retId)] || {};
                                                    const bankName = item.bank_name || userBank.bank_name || userBank.bank || 'HDFC Bank';
                                                    const accNo = item.account_no || userBank.account_no || '50100492817291';

                                                    return (
                                                        <tr
                                                            key={item.id || item.settle_id || index}
                                                            className={`transition-colors duration-100 ${isDarkMode
                                                                ? index % 2 === 0 ? 'bg-[#111827] hover:bg-[#1a2336]' : 'bg-[#0e1422] hover:bg-[#1a2336]'
                                                                : index % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-50'
                                                                }`}
                                                        >
                                                            {/* 1. Master No & UTR */}
                                                            <td className="py-2 px-3 whitespace-nowrap">
                                                                <div className="flex flex-col">
                                                                    <div className="flex items-center gap-1">
                                                                        <span
                                                                            onClick={() => copyToClipboard(item.settle_id || item.id, "Master Settlement No")}
                                                                            className={`text-[10.5px] font-mono font-bold hover:underline cursor-pointer ${isDarkMode ? 'text-indigo-400' : 'text-blue-600'}`}
                                                                            title="Click to copy Master Settlement No"
                                                                        >
                                                                            {item.settle_id || item.id || '—'}
                                                                        </span>
                                                                        <Copy
                                                                            size={9}
                                                                            className="cursor-pointer text-gray-400 hover:text-gray-200"
                                                                            onClick={() => copyToClipboard(item.settle_id || item.id, "Master Settlement No")}
                                                                        />
                                                                    </div>
                                                                    {item.tnx_id ? (
                                                                        <div className="flex items-center gap-1 mt-0.5">
                                                                            <span
                                                                                onClick={() => copyToClipboard(item.tnx_id, "UTR Transaction ID")}
                                                                                className={`text-[9px] font-mono cursor-pointer hover:underline ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}
                                                                                title="Click to copy UTR"
                                                                            >
                                                                                UTR: {item.tnx_id}
                                                                            </span>
                                                                            <Copy
                                                                                size={8}
                                                                                className="cursor-pointer text-gray-400 hover:text-gray-200"
                                                                                onClick={() => copyToClipboard(item.tnx_id, "UTR Transaction ID")}
                                                                            />
                                                                        </div>
                                                                    ) : (
                                                                        <span className="text-[8.5px] text-gray-400 mt-0.5 font-mono">UTR: —</span>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* 2. Retailer & Bank Info */}
                                                            <td className="py-2 px-3 whitespace-nowrap">
                                                                <div className="flex flex-col">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <span className={`text-[10.5px] font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                                                            {retName}
                                                                        </span>
                                                                        <span className={`text-[8.5px] px-1 py-0.2 rounded font-mono ${isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-50 text-indigo-700'}`}>
                                                                            ID: {retId}
                                                                        </span>
                                                                    </div>
                                                                    <span className={`text-[9px] mt-0.5 flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                        <Building2 size={10} className="shrink-0 text-indigo-400" />
                                                                        <span>{bankName}</span>
                                                                        <span>•</span>
                                                                        <span className="font-mono">A/C: {accNo ? `••••${String(accNo).slice(-4)}` : '—'}</span>
                                                                    </span>
                                                                </div>
                                                            </td>

                                                            {/* 3. Status */}
                                                            <td className="py-2 px-3 text-center whitespace-nowrap">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold border ${getStatusBadgeClass(item.status)}`}>
                                                                    <span className={`w-1 h-1 rounded-full mr-1 ${item.status?.toLowerCase() === 'completed' || item.status?.toLowerCase() === 'processed'
                                                                        ? 'bg-emerald-400 animate-pulse'
                                                                        : item.status?.toLowerCase() === 'pending'
                                                                            ? 'bg-amber-400 animate-pulse'
                                                                            : 'bg-gray-400'
                                                                        }`}></span>
                                                                    {item.status || '—'}
                                                                </span>
                                                            </td>

                                                            {/* 4. Settlement Amount */}
                                                            <td className="py-2 px-3 text-right whitespace-nowrap">
                                                                <div className="flex flex-col items-end">
                                                                    <span className={`text-[11.5px] font-extrabold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                                                        ₹{parseFloat(item.remaining_amount || item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                                    </span>
                                                                    {item.amount && parseFloat(item.amount) !== parseFloat(item.remaining_amount || 0) && (
                                                                        <span className="text-[8px] text-gray-400">
                                                                            Gross: ₹{parseFloat(item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* 5. Date */}
                                                            <td className={`py-2 px-3 text-[10px] text-center whitespace-nowrap ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                {formattedDate}
                                                            </td>

                                                            {/* 6. Action */}
                                                            <td className="py-2 px-3 text-center whitespace-nowrap">
                                                                <div className="flex items-center justify-center gap-1.5">
                                                                    <button
                                                                        onClick={() => handleView(item)}
                                                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[9.5px] font-semibold transition-all ${isDarkMode
                                                                            ? 'border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10'
                                                                            : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                                                            }`}
                                                                        title="View Settlement Details"
                                                                    >
                                                                        <Eye size={11} />
                                                                        <span>View</span>
                                                                    </button>
                                                                    {item.status?.toLowerCase() === 'pending' && (
                                                                        <button
                                                                            onClick={() => handleProcess(item)}
                                                                            className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[9.5px] font-semibold transition-all ${isDarkMode
                                                                                ? 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'
                                                                                : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                                                                }`}
                                                                            title="Process Payment"
                                                                        >
                                                                            <FcProcess size={11} />
                                                                            <span>Process</span>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pagination footer */}
                            {activeTab === 'orders' && filteredOrders.length > 0 && (
                                <div className={`flex items-center justify-between px-3 py-1.5 border-t shrink-0 ${isDarkMode ? 'border-gray-800 bg-[#0e1422]' : 'border-gray-200 bg-gray-50'
                                    }`}>
                                    <p className={`text-[9.5px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Showing {Math.min((orderPage - 1) * orderLimit + 1, filteredOrders.length)} - {Math.min(orderPage * orderLimit, filteredOrders.length)} of {filteredOrders.length} orders
                                        {selectedCustomerId && selectedCustomerId !== "ALL" && (
                                            <span className="ml-1 text-indigo-400 font-medium">
                                                (Filtered for {selectedRetailerLabel})
                                            </span>
                                        )}
                                    </p>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => setOrderPage(p => Math.max(1, p - 1))}
                                            disabled={orderPage === 1}
                                            className={`px-2 py-0.5 rounded text-[9.5px] font-medium border ${orderPage === 1
                                                ? 'opacity-40 cursor-not-allowed'
                                                : isDarkMode ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-100'
                                                }`}
                                        >
                                            Prev
                                        </button>
                                        <span className={`text-[9.5px] px-1.5 font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                            {orderPage} / {totalOrderPages}
                                        </span>
                                        <button
                                            onClick={() => setOrderPage(p => Math.min(totalOrderPages, p + 1))}
                                            disabled={orderPage === totalOrderPages}
                                            className={`px-2 py-0.5 rounded text-[9.5px] font-medium border ${orderPage === totalOrderPages
                                                ? 'opacity-40 cursor-not-allowed'
                                                : isDarkMode ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-300 hover:bg-gray-100'
                                                }`}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <ViewRemmitance isopen={isPopupOpen} onClose={handleClose} data={data} />

                {/* ================= ORDER DETAILS MODAL ================= */}
                <Dialog
                    open={Boolean(viewingOrder)}
                    onClose={() => setViewingOrder(null)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '16px',
                            background: isDarkMode ? '#0f172a' : '#ffffff',
                            color: isDarkMode ? '#f8fafc' : '#0f172a',
                            border: isDarkMode ? '1px solid #334155' : '1px solid #e2e8f0',
                            backgroundImage: 'none',
                            p: 0,
                            overflow: 'hidden',
                        }
                    }}
                >
                    {viewingOrder && (
                        <div className="flex flex-col">
                            {/* Modal Header */}
                            <div className={`flex items-center justify-between px-4 py-3 border-b ${
                                isDarkMode ? 'border-slate-800 bg-slate-900/60' : 'border-slate-100 bg-slate-50'
                            }`}>
                                <div className="flex items-center gap-2.5">
                                    <div className={`p-2 rounded-xl ${
                                        isDarkMode ? 'bg-indigo-500/15 text-indigo-400' : 'bg-indigo-100 text-indigo-600'
                                    }`}>
                                        <PackageCheck size={18} />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold leading-tight">
                                            Delivered COD Order Details
                                        </h3>
                                        <div className="flex items-center gap-1.5 mt-0.5 text-[10.5px] font-mono text-slate-400">
                                            <span>Order: {viewingOrder.order_id}</span>
                                            <button
                                                onClick={() => copyToClipboard(viewingOrder.order_id, "Order ID")}
                                                className="hover:text-white"
                                            >
                                                <Copy size={10} />
                                            </button>
                                            {viewingOrder.awb_number && viewingOrder.awb_number !== "—" && (
                                                <>
                                                    <span>•</span>
                                                    <span>AWB: {viewingOrder.awb_number}</span>
                                                    <button
                                                        onClick={() => copyToClipboard(viewingOrder.awb_number, "AWB")}
                                                        className="hover:text-white"
                                                    >
                                                        <Copy size={10} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setViewingOrder(null)}
                                    className={`p-1 rounded-lg transition-colors ${
                                        isDarkMode ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200'
                                    }`}
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-4 space-y-3">
                                {/* Grid Info */}
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className={`p-2.5 rounded-xl border ${isDarkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-slate-50 border-slate-200'}`}>
                                        <span className="text-[9.5px] uppercase font-bold text-slate-400 block mb-0.5">
                                            Retailer
                                        </span>
                                        <p className="font-bold text-indigo-400">
                                            {customerMap[viewingOrder.user_id] || `Retailer #${viewingOrder.user_id}`}
                                        </p>
                                        <p className="text-[10px] text-slate-400">
                                            ID: {viewingOrder.user_id} • Plan: {viewingOrder.userPlan}
                                        </p>
                                    </div>

                                    <div className={`p-2.5 rounded-xl border ${isDarkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-slate-50 border-slate-200'}`}>
                                        <span className="text-[9.5px] uppercase font-bold text-slate-400 block mb-0.5">
                                            Customer
                                        </span>
                                        <p className="font-bold truncate">
                                            {viewingOrder.consignee_name}
                                        </p>
                                        <p className="text-[10px] text-slate-400 font-mono">
                                            {viewingOrder.consignee_phone}
                                        </p>
                                    </div>

                                    <div className={`p-2.5 rounded-xl border ${isDarkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-slate-50 border-slate-200'}`}>
                                        <span className="text-[9.5px] uppercase font-bold text-slate-400 block mb-0.5">
                                            Route
                                        </span>
                                        <p className="font-medium flex items-center gap-1">
                                            <span>{viewingOrder.origin}</span>
                                            <ArrowRight size={11} className="text-indigo-400" />
                                            <span className="font-bold text-indigo-300">{viewingOrder.destination}</span>
                                        </p>
                                    </div>

                                    <div className={`p-2.5 rounded-xl border ${isDarkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-slate-50 border-slate-200'}`}>
                                        <span className="text-[9.5px] uppercase font-bold text-slate-400 block mb-0.5">
                                            Delivery Status
                                        </span>
                                        <div className="flex items-center gap-1.5">
                                            <span className="px-1.5 py-0.2 rounded-full text-[9px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                                                {viewingOrder.status || 'Delivered'}
                                            </span>
                                            <span className="text-[10px] text-slate-400">
                                                {viewingOrder.order_date && viewingOrder.order_date !== "—"
                                                    ? new Date(viewingOrder.order_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                                                    : ""}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Financial Card */}
                                <div className={`p-3.5 rounded-xl border ${
                                    isDarkMode ? 'bg-indigo-950/20 border-indigo-500/30' : 'bg-indigo-50/70 border-indigo-200'
                                }`}>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-400 block mb-2">
                                        Payout Financial Breakdown
                                    </span>
                                    <div className="space-y-1.5 text-xs">
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">Gross COD Collected:</span>
                                            <span className="font-semibold">
                                                ₹{parseFloat(viewingOrder.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-400">
                                                Early Payout Fee ({viewingOrder.userPlan} - {viewingOrder.feePercent}%):
                                            </span>
                                            <span className={`font-semibold ${viewingOrder.feeAmount > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                                                {viewingOrder.feeAmount > 0 ? `-₹${parseFloat(viewingOrder.feeAmount).toFixed(2)}` : '₹0.00 (No Charge)'}
                                            </span>
                                        </div>
                                        <div className="pt-2 border-t border-indigo-500/20 flex justify-between items-baseline">
                                            <span className="font-bold text-sm">Net Payable to Retailer:</span>
                                            <span className="text-lg font-black text-emerald-400">
                                                ₹{parseFloat(viewingOrder.netAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className={`flex items-center justify-end gap-2 px-4 py-2.5 border-t ${
                                isDarkMode ? 'border-slate-800 bg-slate-900/60' : 'border-slate-100 bg-slate-50'
                            }`}>
                                <button
                                    onClick={() => setViewingOrder(null)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                                        isDarkMode ? 'border-slate-700 hover:bg-slate-800 text-slate-300' : 'border-slate-300 hover:bg-slate-200 text-slate-700'
                                    }`}
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => handleSettleSingleOrder(viewingOrder)}
                                    disabled={isSettling}
                                    className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white flex items-center gap-1.5 shadow-sm transition-all"
                                >
                                    {isSettling ? <Spinner /> : <Zap size={13} />}
                                    <span>Settle This Order Now</span>
                                </button>
                            </div>
                        </div>
                    )}
                </Dialog>

                {/* ================= COMPREHENSIVE BANK DISBURSE & SETTLEMENT MODAL ================= */}
                <Dialog
                    open={settlementModalOpen}
                    onClose={() => setSettlementModalOpen(false)}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '20px',
                            background: isDarkMode ? '#0f172a' : '#ffffff',
                            color: isDarkMode ? '#f8fafc' : '#0f172a',
                            border: isDarkMode ? '1px solid #334155' : '1px solid #e2e8f0',
                            backgroundImage: 'none',
                            p: 0,
                            overflow: 'hidden',
                        }
                    }}
                >
                    {settlementModalOpen && (
                        <div className="flex flex-col max-h-[92vh]">
                            {/* Header */}
                            <div className={`flex items-center justify-between px-5 py-3.5 border-b shrink-0 ${
                                isDarkMode
                                    ? 'bg-gradient-to-r from-slate-900 via-indigo-950/40 to-slate-900 border-slate-800'
                                    : 'bg-gradient-to-r from-slate-50 via-indigo-50/40 to-slate-50 border-slate-200'
                            }`}>
                                <div className="flex items-center gap-2.5">
                                    <div className="p-2 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-500 text-white shadow-sm shadow-emerald-500/20">
                                        <Wallet size={18} />
                                    </div>
                                    <div>
                                        <h2 className="text-sm font-bold leading-tight">
                                            Disburse COD Settlement to Retailer Account
                                        </h2>
                                        <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Retailer: <span className="font-semibold text-indigo-400">{settlementForm.retailerName}</span> (ID: #{settlementForm.retailerId}) • {settlementOrdersToProcess.length} Orders
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSettlementModalOpen(false)}
                                    className={`p-1.5 rounded-lg border transition-all ${
                                        isDarkMode ? 'border-slate-800 text-slate-400 hover:text-white hover:bg-slate-800' : 'border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                                    }`}
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Scrollable Body */}
                            <div className="p-5 overflow-y-auto custom-scrollbar space-y-4 text-xs">
                                {/* 1. Retailer Bank Account Details */}
                                <div className={`p-4 rounded-xl border ${
                                    isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-slate-50 border-slate-200'
                                }`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-1.5">
                                            <Building2 size={15} className="text-indigo-400" />
                                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                                                Retailer Bank Account Details (Disburse Target)
                                            </h3>
                                        </div>
                                        <span className="text-[9.5px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-medium">
                                            Verified Beneficiary
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                                                Bank Name
                                            </label>
                                            <input
                                                type="text"
                                                value={settlementForm.bankName}
                                                onChange={(e) => setSettlementForm(prev => ({ ...prev, bankName: e.target.value }))}
                                                className={`w-full px-2.5 py-1.5 text-xs rounded-lg border outline-none font-medium ${
                                                    isDarkMode ? 'bg-slate-800 border-slate-700 text-white focus:border-indigo-500' : 'bg-white border-slate-300 text-slate-900 focus:border-blue-500'
                                                }`}
                                                placeholder="Bank Name"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                                                Account Number
                                            </label>
                                            <input
                                                type="text"
                                                value={settlementForm.accountNo}
                                                onChange={(e) => setSettlementForm(prev => ({ ...prev, accountNo: e.target.value }))}
                                                className={`w-full px-2.5 py-1.5 text-xs rounded-lg border outline-none font-mono font-semibold ${
                                                    isDarkMode ? 'bg-slate-800 border-slate-700 text-emerald-400 focus:border-indigo-500' : 'bg-white border-slate-300 text-emerald-700 focus:border-blue-500'
                                                }`}
                                                placeholder="Account Number"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                                                Account Holder Name
                                            </label>
                                            <input
                                                type="text"
                                                value={settlementForm.accountHolder}
                                                onChange={(e) => setSettlementForm(prev => ({ ...prev, accountHolder: e.target.value }))}
                                                className={`w-full px-2.5 py-1.5 text-xs rounded-lg border outline-none font-medium ${
                                                    isDarkMode ? 'bg-slate-800 border-slate-700 text-white focus:border-indigo-500' : 'bg-white border-slate-300 text-slate-900 focus:border-blue-500'
                                                }`}
                                                placeholder="Beneficiary Name"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                                                IFSC Code
                                            </label>
                                            <input
                                                type="text"
                                                value={settlementForm.ifscCode}
                                                onChange={(e) => setSettlementForm(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
                                                className={`w-full px-2.5 py-1.5 text-xs rounded-lg border outline-none font-mono uppercase ${
                                                    isDarkMode ? 'bg-slate-800 border-slate-700 text-white focus:border-indigo-500' : 'bg-white border-slate-300 text-slate-900 focus:border-blue-500'
                                                }`}
                                                placeholder="IFSC Code"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                                                Transfer Mode
                                            </label>
                                            <select
                                                value={settlementForm.transferMode}
                                                onChange={(e) => {
                                                    const newMode = e.target.value;
                                                    setSettlementForm(prev => ({
                                                        ...prev,
                                                        transferMode: newMode,
                                                        transactionId: prev.transactionId ? generateSampleTxnId(newMode) : ''
                                                    }));
                                                }}
                                                className={`w-full px-2.5 py-1.5 text-xs rounded-lg border outline-none font-medium ${
                                                    isDarkMode ? 'bg-slate-800 border-slate-700 text-white focus:border-indigo-500' : 'bg-white border-slate-300 text-slate-900 focus:border-blue-500'
                                                }`}
                                            >
                                                <option value="NEFT">NEFT (National Electronic Funds Transfer)</option>
                                                <option value="RTGS">RTGS (Real Time Gross Settlement)</option>
                                                <option value="IMPS">IMPS (Immediate Payment Service - 24x7)</option>
                                                <option value="UPI">UPI (Unified Payments Interface / VPA)</option>
                                                <option value="NET_BANKING">Net Banking / Direct Bank Transfer</option>
                                                <option value="CHEQUE">Cheque / Demand Draft</option>
                                            </select>
                                        </div>

                                        {settlementForm.transferMode === 'UPI' && (
                                            <div>
                                                <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                                                    Retailer UPI ID / VPA
                                                </label>
                                                <input
                                                    type="text"
                                                    value={settlementForm.upiId || ''}
                                                    onChange={(e) => setSettlementForm(prev => ({ ...prev, upiId: e.target.value }))}
                                                    className={`w-full px-2.5 py-1.5 text-xs rounded-lg border outline-none font-mono font-semibold ${
                                                        isDarkMode ? 'bg-slate-800 border-slate-700 text-purple-400 focus:border-indigo-500' : 'bg-white border-slate-300 text-purple-700 focus:border-blue-500'
                                                    }`}
                                                    placeholder="retailer@okhdfcbank"
                                                />
                                            </div>
                                        )}

                                        <div>
                                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                                                Retailer Reference
                                            </label>
                                            <div className={`px-2.5 py-1.5 text-xs rounded-lg border font-mono flex items-center justify-between ${
                                                isDarkMode ? 'bg-slate-800/50 border-slate-700 text-slate-300' : 'bg-slate-100 border-slate-200 text-slate-700'
                                            }`}>
                                                <span>ID: #{settlementForm.retailerId}</span>
                                                <span className="text-[9.5px] text-indigo-400 font-semibold">{settlementForm.retailerName}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 2. Audit & Settlement Identifiers */}
                                {(() => {
                                    const paymentMeta = getPaymentMethodDetails(settlementForm.transferMode);
                                    return (
                                        <div className={`p-4 rounded-xl border ${
                                            isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-slate-50 border-slate-200'
                                        }`}>
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-1.5">
                                                    <CreditCard size={15} className="text-purple-400" />
                                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                                                        Master Batch Reference &amp; Payment Transaction Details
                                                    </h3>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <div className="flex items-center gap-1">
                                                            <label className="text-[10px] uppercase font-bold text-slate-400">
                                                                Master Settlement Number (settle_id)
                                                            </label>
                                                            <span className="text-[8px] px-1.5 py-0.2 rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-semibold">
                                                                Auto Batch
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newMaster = `SETTLE-${Math.floor(100000 + Math.random() * 900000)}`;
                                                                    setSettlementForm(prev => ({ ...prev, masterNo: newMaster }));
                                                                    toast.info("New Master Settlement No Generated!");
                                                                }}
                                                                className="text-gray-400 hover:text-indigo-400 flex items-center gap-0.5 text-[9px]"
                                                            >
                                                                <RefreshCw size={9} />
                                                                <span>Regenerate</span>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => copyToClipboard(settlementForm.masterNo, "Master No")}
                                                                className="text-gray-400 hover:text-indigo-400 flex items-center gap-0.5 text-[9px]"
                                                            >
                                                                <Copy size={9} />
                                                                <span>Copy</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        value={settlementForm.masterNo}
                                                        onChange={(e) => setSettlementForm(prev => ({ ...prev, masterNo: e.target.value }))}
                                                        className={`w-full px-2.5 py-1.5 text-xs rounded-lg border outline-none font-mono font-bold ${
                                                            isDarkMode ? 'bg-slate-800 border-slate-700 text-indigo-400 focus:border-indigo-500' : 'bg-white border-slate-300 text-blue-700 focus:border-blue-500'
                                                        }`}
                                                        placeholder="SETTLE-XXXXXX"
                                                    />
                                                    <span className="text-[9px] text-slate-400 mt-1 block">
                                                        Unique internal batch ID generated by system for this settlement payout.
                                                    </span>
                                                </div>

                                                <div>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <label className="text-[10px] uppercase font-bold text-slate-400">
                                                            {paymentMeta.label}
                                                        </label>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const sampleId = generateSampleTxnId(settlementForm.transferMode);
                                                                    setSettlementForm(prev => ({ ...prev, transactionId: sampleId }));
                                                                    toast.info(`Generated Sample ${paymentMeta.samplePrefix} ID`);
                                                                }}
                                                                className="text-indigo-400 hover:text-indigo-300 flex items-center gap-1 text-[9px] font-semibold"
                                                            >
                                                                <Zap size={10} />
                                                                <span>+ Generate Test ID</span>
                                                            </button>
                                                            {settlementForm.transactionId && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => copyToClipboard(settlementForm.transactionId, "Transaction ID")}
                                                                    className="text-gray-400 hover:text-emerald-400 flex items-center gap-0.5 text-[9px]"
                                                                >
                                                                    <Copy size={9} />
                                                                    <span>Copy</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        value={settlementForm.transactionId}
                                                        onChange={(e) => setSettlementForm(prev => ({ ...prev, transactionId: e.target.value }))}
                                                        className={`w-full px-2.5 py-1.5 text-xs rounded-lg border outline-none font-mono font-bold ${
                                                            isDarkMode ? 'bg-slate-800 border-slate-700 text-emerald-400 focus:border-indigo-500' : 'bg-white border-slate-300 text-emerald-700 focus:border-blue-500'
                                                        }`}
                                                        placeholder={paymentMeta.placeholder}
                                                    />
                                                    <span className="text-[9px] text-slate-400 mt-1 block">
                                                        {paymentMeta.hint}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* 3. Disbursement Financial Summary Card */}
                                <div className={`p-4 rounded-xl border ${
                                    isDarkMode ? 'bg-indigo-950/30 border-indigo-500/40' : 'bg-indigo-50/80 border-indigo-200'
                                }`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-[10.5px] font-bold uppercase tracking-wider text-indigo-400">
                                            Disbursement Financial Summary
                                        </span>
                                        <span className="text-[10px] font-mono text-slate-400">
                                            Orders Included: <span className="font-bold text-indigo-300">{settlementOrdersToProcess.length}</span>
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div className={`p-2.5 rounded-lg border ${isDarkMode ? 'bg-black/30 border-slate-800' : 'bg-white border-slate-200'}`}>
                                            <span className="text-[9.5px] uppercase font-bold text-slate-400 block mb-0.5">
                                                Gross COD Amount
                                            </span>
                                            <span className="text-base font-bold text-slate-100 dark:text-slate-100">
                                                ₹{settlementForm.grossAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>

                                        <div className={`p-2.5 rounded-lg border ${isDarkMode ? 'bg-black/30 border-slate-800' : 'bg-white border-slate-200'}`}>
                                            <span className="text-[9.5px] uppercase font-bold text-slate-400 block mb-0.5">
                                                Plan Deductions
                                            </span>
                                            <span className={`text-base font-bold ${settlementForm.feeAmount > 0 ? (isDarkMode ? 'text-amber-400' : 'text-amber-600') : 'text-emerald-400'}`}>
                                                {settlementForm.feeAmount > 0 ? `-₹${settlementForm.feeAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '₹0.00 (0% Fee)'}
                                            </span>
                                        </div>

                                        <div className={`p-2.5 rounded-lg border ${isDarkMode ? 'bg-emerald-950/40 border-emerald-500/40' : 'bg-emerald-50 border-emerald-300'}`}>
                                            <span className="text-[9.5px] uppercase font-bold text-emerald-400 block mb-0.5">
                                                Final Net Disbursed to Account
                                            </span>
                                            <span className="text-xl font-black text-emerald-400">
                                                ₹{settlementForm.netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* 4. Itemized Orders Preview Table */}
                                <div className="space-y-1.5">
                                    <div className="flex items-center justify-between text-[10.5px]">
                                        <span className="font-bold uppercase tracking-wider text-slate-400">
                                            Orders Being Settled ({settlementOrdersToProcess.length})
                                        </span>
                                        <span className="font-mono text-emerald-400 font-bold">
                                            Total Net: ₹{settlementForm.netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>

                                    <div className={`rounded-xl border overflow-hidden max-h-48 overflow-y-auto custom-scrollbar ${
                                        isDarkMode ? 'border-slate-800 bg-slate-900/60' : 'border-slate-200 bg-white'
                                    }`}>
                                        <table className="w-full text-left border-collapse text-[11px]">
                                            <thead className={`sticky top-0 z-10 text-[9px] uppercase font-bold ${
                                                isDarkMode ? 'bg-slate-800 text-slate-300 border-b border-slate-700' : 'bg-slate-100 text-slate-600 border-b border-slate-200'
                                            }`}>
                                                <tr>
                                                    <th className="py-1.5 px-3">Order ID &amp; AWB</th>
                                                    <th className="py-1.5 px-3">Consignee &amp; Route</th>
                                                    <th className="py-1.5 px-3 text-right">Gross COD</th>
                                                    <th className="py-1.5 px-3 text-right">Net Payable</th>
                                                </tr>
                                            </thead>
                                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-800' : 'divide-slate-100'}`}>
                                                {settlementOrdersToProcess.map((ord, i) => (
                                                    <tr key={ord.order_id || i} className={isDarkMode ? 'hover:bg-slate-800/40' : 'hover:bg-slate-50'}>
                                                        <td className="py-1.5 px-3 whitespace-nowrap">
                                                            <div className="font-mono font-bold text-indigo-400">{ord.order_id}</div>
                                                            <div className="text-[9px] text-slate-400 font-mono">AWB: {ord.awb_number || '—'}</div>
                                                        </td>
                                                        <td className="py-1.5 px-3 whitespace-nowrap">
                                                            <div className="font-medium text-slate-200">{ord.consignee_name}</div>
                                                            <div className="text-[9px] text-slate-400">{ord.origin} → {ord.destination}</div>
                                                        </td>
                                                        <td className="py-1.5 px-3 text-right font-medium whitespace-nowrap">
                                                            ₹{parseFloat(ord.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                        </td>
                                                        <td className="py-1.5 px-3 text-right font-bold text-emerald-400 whitespace-nowrap">
                                                            ₹{parseFloat(ord.netAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Audit & Compliance Notice */}
                                <div className={`flex items-start gap-2 p-2.5 rounded-xl border text-[10.5px] ${
                                    isDarkMode ? 'bg-emerald-950/20 border-emerald-500/30 text-emerald-200/90' : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                }`}>
                                    <ShieldCheck size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                                    <span>
                                        Confirming will immediately mark these {settlementOrdersToProcess.length} orders as Settled, generate the official settlement batch, credit the retailer's bank account via {settlementForm.transferMode}, and reflect Master No #{settlementForm.masterNo} and Bank UTR #{settlementForm.transactionId} in Previous COD History.
                                    </span>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className={`flex items-center justify-between px-5 py-3 border-t shrink-0 ${
                                isDarkMode ? 'border-slate-800 bg-slate-900/80' : 'border-slate-200 bg-slate-50'
                            }`}>
                                <div className="text-[11px] font-mono">
                                    <span className="text-slate-400">Total Payout: </span>
                                    <span className="font-extrabold text-emerald-500">
                                        ₹{settlementForm.netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </span>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setSettlementModalOpen(false)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                                            isDarkMode ? 'border-slate-700 hover:bg-slate-800 text-slate-300' : 'border-slate-300 hover:bg-slate-200 text-slate-700'
                                        }`}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleConfirmDisburseAndSettle}
                                        disabled={isSettling}
                                        className="px-4 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 flex items-center gap-1.5 shadow-md shadow-emerald-500/20 cursor-pointer transition-all"
                                    >
                                        {isSettling ? <Spinner /> : <Zap size={13} />}
                                        <span>Confirm Disburse &amp; Settle ({settlementOrdersToProcess.length} Orders)</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </Dialog>
            </div>
        </>
    );
};

export default RemittanceList;